import { ChangeEvent, ReactNode, useContext, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill, { PillTone, WarningIcon } from '../components/ui/Pill';
import MoreMenu from '../components/ui/MoreMenu';
import Checklist, { timelineCounts, useChecklist } from '../components/Checklist';
import { Lead } from '../components/ui/Panel';
import { useEventComms, useEventInfo } from '../lib/eventInfo';
import { SharedTextsDialog, gapText, parkingMissingEverywhere } from '../components/events/info/InfoPackagesPanel';
import { EmptyState, SkeletonRows, ErrorNotice, InfoNotice } from '../components/ui/States';
import { fmt } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth, useCanSee } from '../contexts/AuthContext';
import { EditionSummary } from '../lib/forms';
import { label, personName, errorMessage, inputClass, selectClass, toCents } from '../lib/programs';
import { openBlankTab, showPrintable } from '../lib/printable';
import {
  CheckinLink,
  CountRow,
  EventDay,
  EventDetail,
  EventListItem,
  EventStats,
  EventView,
  Invitee,
  KIND_LABEL,
  Needs,
  SOURCE_LABEL,
  TRAVEL_LABEL,
  Audience,
  audienceName,
  dayLabel,
  cameFrom,
  splitList,
  torontoToday,
} from '../lib/events';
import EventShell from '../components/events/EventShell';
import LockBanner from '../components/events/LockBanner';
import EventListSections, { listLead } from '../components/events/EventListSections';
import EventInsights from '../components/events/insights/EventInsights';
import StartNextYear from '../components/events/StartNextYear';
import OutreachPage, { OutreachForm } from '../components/events/Outreach';
import PiMeetingPage from '../components/events/PiMeetingPage';
import AudiencesPanel from '../components/events/AudiencesPanel';
import { registerPanel, registerSlot, slotFor } from '../components/events/registry';
import { ShowAllTabs, useEventContent } from '../components/events/pageContext';
import { panelVisible } from '../contracts';
import type { FollowupItem, Followups, PanelProps } from '../components/events/types';
import Help from '../components/ui/Help';
import { confirmAction } from '../components/ui/Confirm';
import { th as cth, td as ctd, headRow as cheadRow, num as cnum, em as cem } from '../components/events/logistics';

const STATUSES = ['invited', 'registered', 'confirmed', 'declined', 'waitlisted', 'attended', 'no_show'];
const th = 'py-2 pr-4';
const td = 'py-[9px] pr-4 align-top';
const headRow = 'text-left text-small text-ink-2 border-b border-line-strong';
const SPREADSHEET = /\.(xlsx|xls|xlsm|numbers)$/i;

function CountTable({ title, rows }: { title: string; rows: CountRow[] }) {
  return (
    <div>
      <div className="text-small text-ink-2 mb-1">{title}</div>
      {!rows.length ? (
        <p className="text-small text-ink-2">None</p>
      ) : (
        <ul className="text-table">
          {rows.map((r) => (
            <li key={r.value} className="flex justify-between border-b border-line py-1">
              <span>{r.value}</span>
              <span>{fmt(r.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- event form

interface DayForm {
  date: string;
  label: string;
  startTime: string;
  endTime: string;
  dinner: boolean;
}

const emptyDay = (): DayForm => ({ date: '', label: '', startTime: '09:00', endTime: '17:00', dinner: false });

interface WebsiteEvent {
  id: string;
  title: string;
  startsAt: string | null;
  endsAt: string | null;
  location: string | null;
  mode: string | null;
  fromWebsite: boolean;
}
const torontoParts = (iso: string) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
const torontoDate = (iso: string) => {
  const p = torontoParts(iso);
  return `${p.year}-${p.month}-${p.day}`;
};
const torontoTime = (iso: string) => {
  const p = torontoParts(iso);
  return `${p.hour}:${p.minute}`;
};
const addYears = (date: string, n: number) => `${Number(date.slice(0, 4)) + n}${date.slice(4)}`;

/** A labelled field of the event dialog: label above (15/600 ink), the control, an optional hint below. */
function EfField({
  id,
  title,
  hint,
  wide = false,
  children,
}: {
  id: string;
  title: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-[6px] min-w-0 ${wide ? 'md:col-span-2' : ''}`}>
      <label htmlFor={id} className="text-[15px] leading-5 font-semibold text-ink">
        {title}
      </label>
      {children}
      {hint ? (
        <p id={`${id}-hint`} className="text-small text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const efGrid = 'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4';
const efFold =
  'mt-4 border-t border-line pt-3 [&>summary]:cursor-pointer [&>summary]:text-accent [&>summary]:font-semibold [&>summary]:w-fit hover:[&>summary]:text-ink';
const efDayCell =
  'py-2 pr-2 align-middle max-md:block max-md:py-1 max-md:pr-0 max-md:before:content-[attr(data-label)] max-md:before:block max-md:before:text-small max-md:before:text-ink-3';
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function EventForm({
  event,
  onClose,
  onSaved,
  onOutreach,
}: {
  event: EventView | null;
  onClose: () => void;
  onSaved: (id: string) => void;
  onOutreach?: () => void;
}) {
  const [title, setTitle] = useState(event?.title ?? '');
  const [titleFr, setTitleFr] = useState(event?.titleFr ?? '');
  const [kind, setKind] = useState(event?.kind ?? 'national');
  const [mode, setMode] = useState(event?.mode ?? 'in_person');
  const [city, setCity] = useState(event?.city ?? '');
  const [venue, setVenue] = useState(event?.venue ?? '');
  const [formLink, setFormLink] = useState(event?.formLink ?? '');
  const [evaluationUrl, setEvaluationUrl] = useState(event?.evaluationUrl ?? '');
  const [contactEmail, setContactEmail] = useState(event?.contactEmail ?? '');
  const [days, setDays] = useState<DayForm[]>(
    event?.days.map((d: EventDay) => ({ ...d, label: d.label ?? '' })) ?? [emptyDay()],
  );
  const s = event?.settings;
  const [settings, setSettings] = useState({
    hotelMinTravelHours: String(s?.hotelMinTravelHours ?? 1),
    extraNightMinFlightHours: String(s?.extraNightMinFlightHours ?? 3),
    hotelNights: String(s?.hotelNights ?? 1),
    capacity: s?.capacity ? String(s.capacity) : '',
  });
  const [fromId, setFromId] = useState('');
  const candidates = useQuery<WebsiteEvent[]>(
    ['event-website-candidates'],
    async () =>
      (await api.get('/events/website-candidates', { params: { since: addYears(torontoToday(), -1) } })).data.data,
    { enabled: !event },
  );
  const pick = (id: string) => {
    setFromId(id);
    const c = candidates.data?.find((x) => x.id === id);
    if (!c) return;
    setTitle(c.title);
    if (c.location && !city) setCity(c.location);
    if (c.startsAt)
      setDays([
        {
          ...emptyDay(),
          date: torontoDate(c.startsAt),
          startTime: torontoTime(c.startsAt),
          endTime: c.endsAt ? torontoTime(c.endsAt) : '17:00',
        },
      ]);
  };
  const save = useMutation(
    async () => {
      const body = {
        ...(fromId ? { eventId: fromId } : {}),
        title,
        titleFr: titleFr || null,
        kind,
        mode,
        city: city || null,
        venue: venue || null,
        formLink: formLink || null,
        evaluationUrl: evaluationUrl || null,
        contactEmail: contactEmail || null,
        days: days.map((d) => ({
          date: d.date,
          label: d.label || null,
          startTime: d.startTime,
          endTime: d.endTime,
          dinner: d.dinner,
        })),
        settings: {
          hotelMinTravelHours: Number(settings.hotelMinTravelHours),
          extraNightMinFlightHours: Number(settings.extraNightMinFlightHours),
          hotelNights: Number(settings.hotelNights),
          capacity: settings.capacity ? Number(settings.capacity) : null,
        },
      };
      const res = event ? await api.patch(`/events/${event.id}`, body) : await api.post('/events', body);
      return res.data.data as EventDetail;
    },
    { onSuccess: (d) => onSaved(d.event.id) },
  );
  const setDay = (i: number, patch: Partial<DayForm>) =>
    setDays((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const missing = [!title ? 'a title' : null, days.some((d) => !d.date) ? 'a date for every day' : null].filter(
    Boolean,
  );
  const travelSummary = [
    `Hotel only beyond ${plural(Number(settings.hotelMinTravelHours) || 0, 'hour', 'hours')} of travel`,
    `an extra night for flights of ${plural(Number(settings.extraNightMinFlightHours) || 0, 'hour', 'hours')} or more`,
    plural(Number(settings.hotelNights) || 0, 'hotel night', 'hotel nights'),
    `capacity: ${settings.capacity ? fmt(Number(settings.capacity)) : 'none'}`,
  ].join(' · ');
  const linksSet = Boolean(event?.formLink || event?.evaluationUrl || event?.contactEmail);
  return (
    <Modal
      title={event ? 'Edit event' : 'New event'}
      why={
        event
          ? 'Who is invited, which days they come and whose travel is covered are set on People, under Audiences.'
          : 'It starts with the audiences of the latest event of the same kind (or core members and the public). You can change them later on People.'
      }
      onClose={onClose}
      width={800}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading || missing.length > 0} onClick={() => save.mutate()}>
            {save.isLoading ? 'Saving…' : event ? 'Save the changes' : 'Create the event'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {missing.length ? (
            <p className="basis-full text-small text-ink-3">
              {event ? 'Save' : 'Create'} turns on once there&rsquo;s {missing.join(' and ')}.
            </p>
          ) : null}
        </>
      }
    >
      <div className={efGrid}>
        {!event && candidates.data?.length ? (
          <EfField
            id="ef-website"
            title="Start from an event on the website"
            hint="Optional. Picking one fills in the title, city and first day."
            wide
          >
            <select
              id="ef-website"
              aria-describedby="ef-website-hint"
              className={inputClass}
              value={fromId}
              onChange={(e) => pick(e.target.value)}
            >
              <option value="">A new event, not on the website yet</option>
              {candidates.data.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.startsAt ? `${dayLabel(torontoDate(c.startsAt))} · ` : ''}
                  {c.title}
                  {c.mode ? ` (${c.mode})` : ''}
                </option>
              ))}
            </select>
          </EfField>
        ) : null}
        <EfField id="ef-title" title="Title in English">
          <input id="ef-title" className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
        </EfField>
        <EfField id="ef-title-fr" title="Title in French">
          <input id="ef-title-fr" className={inputClass} value={titleFr} onChange={(e) => setTitleFr(e.target.value)} />
        </EfField>
        <EfField id="ef-kind" title="Kind">
          <select
            id="ef-kind"
            className={inputClass}
            value={kind}
            onChange={(e) => (e.target.value === 'outreach' && onOutreach ? onOutreach() : setKind(e.target.value))}
          >
            {Object.entries(KIND_LABEL)
              .filter(([k]) => k !== 'outreach' || kind === 'outreach' || onOutreach)
              .map(([k, v]) => (
                <option key={k} value={k}>
                  {k === 'national' ? 'National meeting' : k === 'outreach' ? 'Outreach talk or visit' : v}
                </option>
              ))}
          </select>
        </EfField>
        <EfField id="ef-mode" title="Mode">
          <select id="ef-mode" className={inputClass} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="in_person">In person</option>
            <option value="hybrid">Hybrid (also on Zoom)</option>
          </select>
        </EfField>
        <EfField id="ef-city" title="City">
          <input id="ef-city" className={inputClass} value={city} onChange={(e) => setCity(e.target.value)} />
        </EfField>
        <EfField id="ef-venue" title="Venue">
          <input id="ef-venue" className={inputClass} value={venue} onChange={(e) => setVenue(e.target.value)} />
        </EfField>
      </div>

      <p className="mt-6 mb-2 text-[15px] leading-5 font-semibold text-ink">Days, Toronto time</p>
      <table className="w-full text-table max-md:block">
        <thead className="max-md:hidden">
          <tr className={headRow}>
            <th className={`${th} w-[160px]`}>Date</th>
            <th className={th}>Label</th>
            <th className={`${th} w-[120px]`}>From</th>
            <th className={`${th} w-[120px]`}>To</th>
            <th className={`${th} w-[48px]`}>
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody className="max-md:block">
          {days.map((d, i) => (
            <tr key={i} className="border-b border-line max-md:block max-md:py-3">
              <td className={efDayCell} data-label="Date">
                <input
                  type="date"
                  aria-label={`Date of day ${i + 1}`}
                  className={inputClass}
                  value={d.date}
                  onChange={(e) => setDay(i, { date: e.target.value })}
                />
              </td>
              <td className={efDayCell} data-label="Label">
                <input
                  aria-label={`Label of day ${i + 1}`}
                  className={inputClass}
                  placeholder="Core members"
                  value={d.label}
                  onChange={(e) => setDay(i, { label: e.target.value })}
                />
              </td>
              <td className={efDayCell} data-label="From">
                <input
                  type="time"
                  aria-label={`Start of day ${i + 1}`}
                  className={inputClass}
                  value={d.startTime}
                  onChange={(e) => setDay(i, { startTime: e.target.value })}
                />
              </td>
              <td className={efDayCell} data-label="To">
                <input
                  type="time"
                  aria-label={`End of day ${i + 1}`}
                  className={inputClass}
                  value={d.endTime}
                  onChange={(e) => setDay(i, { endTime: e.target.value })}
                />
              </td>
              <td className={`${efDayCell} max-md:inline-block`}>
                <Button
                  variant="icon"
                  size="sm"
                  aria-label={`Remove day ${i + 1}`}
                  onClick={() => setDays((ds) => ds.filter((_, j) => j !== i))}
                  disabled={days.length === 1}
                >
                  ×
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="quiet" className="mt-1 !px-0" onClick={() => setDays((ds) => [...ds, emptyDay()])}>
        Add a day
      </Button>

      <details className={efFold}>
        <summary>Travel rules</summary>
        <p className="text-small text-ink-3 mt-1">{travelSummary}. Defaults shown; change any of them.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-4 mt-3">
          <EfField id="ef-hotel-min" title="Hotel only beyond (hours)">
            <input
              id="ef-hotel-min"
              inputMode="decimal"
              className={inputClass}
              value={settings.hotelMinTravelHours}
              onChange={(e) => setSettings({ ...settings, hotelMinTravelHours: e.target.value })}
            />
          </EfField>
          <EfField id="ef-extra-night" title="Extra night from flight (hours)">
            <input
              id="ef-extra-night"
              inputMode="decimal"
              className={inputClass}
              value={settings.extraNightMinFlightHours}
              onChange={(e) => setSettings({ ...settings, extraNightMinFlightHours: e.target.value })}
            />
          </EfField>
          <EfField id="ef-nights" title="Hotel nights">
            <input
              id="ef-nights"
              inputMode="numeric"
              className={inputClass}
              value={settings.hotelNights}
              onChange={(e) => setSettings({ ...settings, hotelNights: e.target.value })}
            />
          </EfField>
          <EfField id="ef-capacity" title="Capacity">
            <input
              id="ef-capacity"
              inputMode="numeric"
              className={inputClass}
              value={settings.capacity}
              onChange={(e) => setSettings({ ...settings, capacity: e.target.value })}
              placeholder="none"
            />
          </EfField>
        </div>
      </details>
      <details className={efFold} open={linksSet}>
        <summary>Links and reply-to address</summary>
        <div className={`${efGrid} mt-3`}>
          <EfField id="ef-form-link" title="Registration form link">
            <input
              id="ef-form-link"
              className={inputClass}
              value={formLink}
              onChange={(e) => setFormLink(e.target.value)}
              placeholder="https://…"
            />
          </EfField>
          <EfField id="ef-evaluation" title="Evaluation survey link">
            <input
              id="ef-evaluation"
              className={inputClass}
              value={evaluationUrl}
              onChange={(e) => setEvaluationUrl(e.target.value)}
              placeholder="https://…"
            />
          </EfField>
          <EfField id="ef-reply-to" title="Reply-to address in the confirmation">
            <input
              id="ef-reply-to"
              className={inputClass}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="divert@yorku.ca"
            />
          </EfField>
        </div>
      </details>
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the event.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- overview

function LiveCounts({ detail }: { detail: EventDetail }) {
  return (
    <table className="w-full text-table">
      <thead>
        <tr className={headRow}>
          <th className={th}>Day</th>
          <th className={th}>Checked in</th>
          <th className={th}>Expected</th>
          {detail.event.audiences.map((a) => (
            <th key={a.key} className={th}>
              {a.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {detail.live.map((d) => (
          <tr key={d.date} className="border-b border-line">
            <td className={td}>
              {dayLabel(d.date)}
              {d.label ? <span className="text-ink-2"> · {d.label}</span> : null}
            </td>
            <td className={`${td} font-semibold text-[18px]`}>{fmt(d.checkedIn)}</td>
            <td className={td}>{fmt(d.expected)}</td>
            {d.byAudience.map((a) => (
              <td key={a.key} className={td}>
                {a.checkedIn} / {a.expected}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// A website form is one Gravity Forms form (an "edition" of a form family). Linking it here
// re-reads every response it already has, so earlier registrations attach too.
const FORM_KINDS = [
  { familyKey: 'event_registration', label: 'Registration form', entry: 'Linked form' },
  { familyKey: 'session_feedback', label: 'Evaluation form', entry: 'Linked evaluation form' },
] as const;
type FormKind = (typeof FORM_KINDS)[number]['familyKey'];

function useFamilyEditions(familyKey: string) {
  return useQuery(['event-form-editions', familyKey], async () => {
    const [family, fresh] = await Promise.all([
      api.get(`/forms/families/${familyKey}`),
      api.get('/forms/editions', { params: { status: 'new' } }),
    ]);
    const inFamily = family.data.data.editions as EditionSummary[];
    const waiting = (fresh.data.data as EditionSummary[]).filter((x) => x.suggestion?.familyKey === familyKey);
    return [...inFamily, ...waiting];
  });
}

const formName = (x: EditionSummary) => `${x.label || x.title || 'Untitled form'} (form ${x.formId})`;
const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric' });

/** Link a website form to this event: the form kind, then the form. Moving one re-reads every response. */
function LinkFormDialog({
  event,
  lists,
  onClose,
  onDone,
}: {
  event: EventView;
  lists: Record<FormKind, EditionSummary[]>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<FormKind>('event_registration');
  const [pick, setPick] = useState('');
  const choices = lists[kind].filter((x) => x.event?.id !== event.id);
  const chosen = choices.find((x) => x.id === pick);
  const save = useMutation(
    async (edition: EditionSummary) =>
      edition.status === 'new'
        ? api.post(`/forms/editions/${edition.id}/accept`, { familyKey: kind, eventId: event.id })
        : api.patch(`/forms/editions/${edition.id}`, { eventId: event.id }),
    { onSuccess: onDone },
  );
  return (
    <Modal
      title="Link a website form"
      why="Its answers attach to this event. Every response the form already has is read again, so earlier answers count too."
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" disabled={!chosen || save.isLoading} onClick={() => chosen && save.mutate(chosen)}>
            {save.isLoading ? 'Linking…' : chosen?.event ? 'Move it here' : 'Link the form'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label htmlFor="link-form-kind" className="block text-[15px] leading-5 font-semibold text-ink mb-1">
            Kind of form
          </label>
          <select
            id="link-form-kind"
            className={`${selectClass} w-full`}
            value={kind}
            onChange={(ev) => {
              setKind(ev.target.value as FormKind);
              setPick('');
            }}
          >
            {FORM_KINDS.map((k) => (
              <option key={k.familyKey} value={k.familyKey}>
                {k.familyKey === 'event_registration'
                  ? 'Registration (status, travel, hotel, dietary, accessibility)'
                  : 'Evaluation (ratings count in the stats)'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="link-form-pick" className="block text-[15px] leading-5 font-semibold text-ink mb-1">
            Website form
          </label>
          <select
            id="link-form-pick"
            className={`${selectClass} w-full`}
            value={pick}
            onChange={(ev) => setPick(ev.target.value)}
          >
            <option value="">{choices.length ? 'Choose a form…' : 'No other forms of this kind'}</option>
            {choices.map((x) => (
              <option key={x.id} value={x.id}>
                {formName(x)}
                {x.status === 'new' ? ' · new form' : ''}
                {x.event
                  ? ` · now on ${x.event.title}${x.event.startsAt ? ` (${shortDay(x.event.startsAt)})` : ''}`
                  : ''}
              </option>
            ))}
          </select>
          {chosen?.event ? (
            <p className="mt-1 text-small text-ink-3">
              Its answers move from {chosen.event.title} to this event (every response is read again).
            </p>
          ) : null}
        </div>
        {save.error ? <ErrorNotice>{errorMessage(save.error, 'Could not link the form.')}</ErrorNotice> : null}
      </div>
    </Modal>
  );
}

/** Event details on the Overview: where, days, travel rules, what is set, the linked forms, related events. */
function Overview({ detail, mutable, refresh }: { detail: EventDetail; mutable: boolean; refresh: () => void }) {
  const e = detail.event;
  const see = useCanSee();
  const [linking, setLinking] = useState(false);
  const registration = useFamilyEditions('event_registration');
  const evaluation = useFamilyEditions('session_feedback');
  const lists: Record<FormKind, EditionSummary[]> = {
    event_registration: registration.data ?? [],
    session_feedback: evaluation.data ?? [],
  };
  const reload = () => {
    registration.refetch();
    evaluation.refetch();
    refresh();
  };
  const unlink = useMutation(
    async (edition: EditionSummary) => api.patch(`/forms/editions/${edition.id}`, { eventId: null }),
    { onSuccess: reload },
  );
  const linked = FORM_KINDS.flatMap((k) =>
    lists[k.familyKey].filter((x) => x.event?.id === e.id).map((x) => ({ x, entry: k.entry })),
  );
  const canLink = FORM_KINDS.some((k) => lists[k.familyKey].some((x) => x.event?.id !== e.id));
  const funded = e.audiences.filter((a) => a.funded).map((a) => a.name);
  const s = e.settings;
  const missing = [e.formLink ? null : 'public registration link', e.evaluationUrl ? null : 'evaluation survey'].filter(
    Boolean,
  );
  const dt = 'text-small text-ink-3';
  const dd = 'mt-[2px] text-[15px] leading-[22px] text-ink';
  const where = [e.venue, e.city].filter(Boolean).join(', ');
  return (
    <section className="mt-14 pt-6 border-t border-line-strong max-md:mt-9 max-md:pt-4">
      <h2 className="font-serif font-semibold text-heading text-ink mb-4">Event details</h2>
      <dl className="grid gap-x-12 gap-y-6 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
        <div>
          <dt className={dt}>Where</dt>
          <dd className={dd}>{where || <span className="text-ink-3">–</span>}</dd>
        </div>
        <div>
          <dt className={dt}>Days</dt>
          <dd className={dd}>
            {e.days.map((d) => (
              <div key={d.date}>
                {dayLabel(d.date)}, {d.startTime}–{d.endTime}
                {d.label ? ` · ${d.label}` : ''}
                {d.dinner ? ' · dinner' : ''}
              </div>
            ))}
          </dd>
        </div>
        <div>
          <dt className={dt}>Travel rules</dt>
          <dd className={dd}>
            Hotel if more than {s.hotelMinTravelHours} {Number(s.hotelMinTravelHours) === 1 ? 'hour' : 'hours'} away ·{' '}
            {s.hotelNights} {Number(s.hotelNights) === 1 ? 'night' : 'nights'} · an extra night for flights of{' '}
            {s.extraNightMinFlightHours} hours or more · travel covered for{' '}
            {funded.length ? funded.join(', ') : 'nobody'}
          </dd>
        </div>
        {s.capacity ? (
          <div>
            <dt className={dt}>Capacity</dt>
            <dd className={dd}>
              {fmt(s.capacity)}
              <span className="block text-small text-ink-3">Public registrations beyond it are waitlisted</span>
            </dd>
          </div>
        ) : null}
        {e.formLink ? (
          <div>
            <dt className={dt}>Public registration link</dt>
            <dd className={`${dd} break-all`}>
              <a
                className="text-accent underline underline-offset-[3px]"
                href={e.formLink}
                target="_blank"
                rel="noreferrer"
              >
                {e.formLink}
              </a>
            </dd>
          </div>
        ) : null}
        {e.evaluationUrl ? (
          <div>
            <dt className={dt}>Evaluation survey</dt>
            <dd className={`${dd} break-all`}>
              <a
                className="text-accent underline underline-offset-[3px]"
                href={e.evaluationUrl}
                target="_blank"
                rel="noreferrer"
              >
                {e.evaluationUrl}
              </a>
            </dd>
          </div>
        ) : null}
        {linked.map(({ x, entry }) => (
          <div key={x.id}>
            <dt className={dt}>{entry}</dt>
            <dd className={`${dd} flex items-start justify-between gap-3`}>
              <span className="min-w-0">
                {formName(x)}
                <span className="block text-small text-ink-3">
                  {x.entries === 1 ? '1 response' : `${fmt(x.entries)} responses`}
                  {x.lastEntryAt ? `, latest ${shortDay(x.lastEntryAt)}` : ''}
                  {x.newQuestions
                    ? ` · ${x.newQuestions} ${x.newQuestions === 1 ? 'question' : 'questions'} not mapped yet, ${
                        see('forms') ? 'decide them on Website forms' : 'a platform administrator maps them'
                      }`
                    : ''}
                </span>
              </span>
              {mutable ? (
                <MoreMenu
                  label={`More for ${formName(x)}`}
                  items={[
                    {
                      label: 'Unlink from this event',
                      destructive: true,
                      disabled: unlink.isLoading,
                      onClick: async () => {
                        if (
                          await confirmAction(
                            `Unlink ${formName(x)}? Its answers stop attaching to this event's people.`,
                            'Unlink the form',
                          )
                        )
                          unlink.mutate(x);
                      },
                    },
                  ]}
                />
              ) : null}
            </dd>
          </div>
        ))}
        {detail.subEvents?.length ? (
          <div>
            <dt className={dt}>Related</dt>
            <dd className={dd}>
              {detail.subEvents.map((sub) => (
                <div key={sub.id}>
                  <Link
                    className="text-accent underline underline-offset-[3px] hover:text-ink"
                    to={`/events/${sub.id}`}
                  >
                    {sub.title}
                  </Link>
                  {sub.days[0] ? <span className="text-ink-2">, {dayLabel(sub.days[0].date)}</span> : null}
                </div>
              ))}
            </dd>
          </div>
        ) : null}
      </dl>
      {registration.error || evaluation.error ? (
        <div className="mt-4">
          <ErrorNotice>Could not load the website forms.</ErrorNotice>
        </div>
      ) : null}
      {unlink.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(unlink.error, 'Could not unlink the form.')}</ErrorNotice>
        </div>
      ) : null}
      {missing.length || !linked.length || (mutable && canLink) ? (
        <p className="mt-6 text-[15px] leading-[22px] text-ink-2">
          {missing.length ? (
            <>
              Not set yet: {missing.join(', ')}
              {mutable ? ' (Edit event, at the top)' : ''}.{' '}
            </>
          ) : null}
          {!linked.length ? 'No website form is linked yet. ' : null}
          {mutable && canLink ? (
            <button
              type="button"
              className="text-accent underline underline-offset-[3px] hover:text-ink"
              onClick={() => setLinking(true)}
            >
              {linked.length ? 'Link another form' : 'Link a form'}
            </button>
          ) : null}
        </p>
      ) : null}
      {linking ? (
        <LinkFormDialog
          event={e}
          lists={lists}
          onClose={() => setLinking(false)}
          onDone={() => {
            setLinking(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------- invitees

/** How each status reads on People: Registered is a teal dot, no answer a hollow ring, declined a dash. */
const STATUS_VIEW: Record<string, [string, PillTone]> = {
  invited: ['No answer yet', 'planned'],
  registered: ['Registered', 'active'],
  confirmed: ['Confirmed', 'active'],
  declined: ['Declined', 'inactive'],
  waitlisted: ['Waitlisted', 'planned'],
  attended: ['Attended', 'done'],
  no_show: ['No-show', 'inactive'],
};
const statusWord = (s: string) => STATUS_VIEW[s]?.[0] ?? label(s);

function StatusTag({ status, small = false }: { status: string; small?: boolean }) {
  const [word, tone] = STATUS_VIEW[status] ?? [label(status), 'planned'];
  return (
    <Pill tone={tone} small={small}>
      {word}
    </Pill>
  );
}

/** A labelled field in the People and Emails dialogs: label above, hint below. */
function PField({
  title,
  children,
  wide = false,
  hint,
}: {
  title: string;
  children: ReactNode;
  wide?: boolean;
  hint?: ReactNode;
}) {
  return (
    <div className={wide ? 'col-span-2 max-md:col-span-1' : ''}>
      <label className="block text-[15px] leading-5 font-semibold text-ink mb-[6px]">{title}</label>
      {children}
      {hint ? <p className="text-small text-ink-3 mt-1">{hint}</p> : null}
    </div>
  );
}

const pGrid = 'grid grid-cols-2 gap-x-5 gap-y-4 max-md:grid-cols-1';

/** One table cell that stacks on a phone, prefixed with its column name. */
const stackCell = `${ctd} max-md:block max-md:py-0 max-md:pr-0 max-md:empty:hidden max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-small max-md:before:text-ink-3`;
const stackRow = 'border-b border-line max-md:block max-md:py-3';

function needsSummary(n: Needs = {}) {
  const parts: string[] = [];
  if (n.attendanceMode === 'virtual') parts.push('online');
  if (n.travelMode) parts.push(TRAVEL_LABEL[n.travelMode] ?? n.travelMode);
  if (n.hotel) parts.push('hotel');
  if (n.parking) parts.push('parking');
  if (n.dietary) parts.push(`diet: ${n.dietary}`);
  if (n.accessibility) parts.push(`access: ${n.accessibility}`);
  if (n.emhic) parts.push('eMHIC');
  const text = parts.join(' · ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

const firstName = (i: Invitee) => personName(i.person).split(/\s+/)[0];

function InviteeModal({
  invitee,
  detail,
  mutable,
  onClose,
  onSaved,
}: {
  invitee: Invitee;
  detail: EventDetail;
  mutable: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const n = invitee.needs ?? {};
  const [form, setForm] = useState({
    audienceKey: invitee.audienceKey ?? '',
    role: invitee.role ?? '',
    status: invitee.status,
    note: invitee.note ?? '',
    attendanceMode: n.attendanceMode ?? '',
    attendDays: n.attendDays ?? [],
    hotel: n.hotel === undefined ? '' : n.hotel ? 'yes' : 'no',
    travelMode: n.travelMode ?? '',
    travelHours: n.travelHours === undefined ? '' : String(n.travelHours),
    flightHours: n.flightHours === undefined ? '' : String(n.flightHours),
    nights: n.nights === undefined ? '' : String(n.nights),
    parking: n.parking === undefined ? '' : n.parking ? 'yes' : 'no',
    dietary: n.dietary ?? '',
    accessibility: n.accessibility ?? '',
    travellingFrom: n.travellingFrom ?? '',
    bankingOnFile: n.bankingOnFile ?? '',
  });
  const yn = (v: string) => (v === '' ? null : v === 'yes');
  const save = useMutation(
    async () =>
      api.patch(`/events/invitees/${invitee.id}`, {
        ...(form.audienceKey ? { audienceKey: form.audienceKey } : {}),
        role: form.role || null,
        status: form.status,
        note: form.note || null,
        needs: {
          attendanceMode: form.attendanceMode || null,
          attendDays: form.attendDays.length ? form.attendDays : null,
          hotel: yn(form.hotel),
          travelMode: form.travelMode || null,
          travelHours: form.travelHours || null,
          flightHours: form.flightHours || null,
          nights: form.nights || null,
          parking: yn(form.parking),
          dietary: form.dietary || null,
          accessibility: form.accessibility || null,
          travellingFrom: form.travellingFrom || null,
          bankingOnFile: form.bankingOnFile || null,
        },
      }),
    { onSuccess: onSaved },
  );
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });
  const toggleDay = (date: string) =>
    setForm({
      ...form,
      attendDays: form.attendDays.includes(date)
        ? form.attendDays.filter((d) => d !== date)
        : [...form.attendDays, date],
    });
  const yesNo = (k: 'hotel' | 'parking') => (
    <select className={inputClass} value={form[k]} onChange={set(k)} disabled={!mutable}>
      <option value="">Not given</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
  const flags = invitee.eligibility?.flags ?? [];
  return (
    <Modal
      title={personName(invitee.person)}
      why={
        mutable
          ? 'Their answers and needs. Saving checks the travel rules again.'
          : 'Their answers and needs, as they gave them.'
      }
      onClose={onClose}
      width={760}
      footer={
        mutable ? (
          <>
            <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
              {save.isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <p className="text-small text-ink-3">
        {invitee.person.email ?? 'No email'} · {invitee.affiliation ?? 'no affiliation given'} · added by{' '}
        {label(invitee.source)}
        {invitee.checkinCode ? ` · check-in code ${invitee.checkinCode}` : ''}
      </p>
      {flags.length ? (
        <ul className="mt-3 text-[15px] leading-[22px] text-ink">
          {flags.map((f) => (
            <li key={f.rule}>
              {f.severity === 'fail' ? 'Outside the travel rules: ' : 'To check: '}
              {f.message}
            </li>
          ))}
        </ul>
      ) : null}
      {invitee.eligibility ? (
        <p className="text-[15px] mt-2">
          Hotel: {invitee.eligibility.nights} night{invitee.eligibility.nights === 1 ? '' : 's'}
          {invitee.eligibility.extraNight ? ', including an extra night' : ''} · travel{' '}
          {invitee.eligibility.funded ? 'covered' : 'not covered'}
        </p>
      ) : null}
      <div className={`${pGrid} mt-5`}>
        <PField title="Audience">
          <select className={inputClass} value={form.audienceKey} onChange={set('audienceKey')} disabled={!mutable}>
            {detail.event.audiences.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
        </PField>
        <PField title="Role">
          <input className={inputClass} value={form.role} onChange={set('role')} disabled={!mutable} />
        </PField>
        <PField title="Status">
          <select className={inputClass} value={form.status} onChange={set('status')} disabled={!mutable}>
            {detail.statuses.map((s) => (
              <option key={s} value={s}>
                {statusWord(s)}
              </option>
            ))}
          </select>
        </PField>
        <PField title="Attending">
          <select
            className={inputClass}
            value={form.attendanceMode}
            onChange={set('attendanceMode')}
            disabled={!mutable}
          >
            <option value="">Not given</option>
            <option value="in_person">In person</option>
            <option value="virtual">Online</option>
          </select>
        </PField>
        {detail.event.days.length > 1 ? (
          <PField title="Attending days" wide hint="None ticked: their speaking day or their audience's days.">
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-[15px]">
              {detail.event.days.map((d) => (
                <label key={d.date} className="flex items-center gap-2 min-h-[36px]">
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px] accent-accent"
                    checked={form.attendDays.includes(d.date)}
                    onChange={() => toggleDay(d.date)}
                    disabled={!mutable}
                  />
                  {dayLabel(d.date)}
                </label>
              ))}
            </div>
          </PField>
        ) : null}
        <PField title="Travel by">
          <select className={inputClass} value={form.travelMode} onChange={set('travelMode')} disabled={!mutable}>
            <option value="">Not given</option>
            {detail.travelModes.map((m) => (
              <option key={m} value={m}>
                {TRAVEL_LABEL[m] ?? m}
              </option>
            ))}
          </select>
        </PField>
        <PField title="Travelling from">
          <input
            className={inputClass}
            value={form.travellingFrom}
            onChange={set('travellingFrom')}
            disabled={!mutable}
          />
        </PField>
        <PField title="Travel time (hours)">
          <input className={inputClass} value={form.travelHours} onChange={set('travelHours')} disabled={!mutable} />
        </PField>
        <PField title="Flight time (hours)">
          <input className={inputClass} value={form.flightHours} onChange={set('flightHours')} disabled={!mutable} />
        </PField>
        <PField title="Nights asked for">
          <input className={inputClass} value={form.nights} onChange={set('nights')} disabled={!mutable} />
        </PField>
        <PField title="Hotel">{yesNo('hotel')}</PField>
        <PField title="Parking pass">{yesNo('parking')}</PField>
        <PField title="Banking information on file" hint="Status only, never the details.">
          <select className={inputClass} value={form.bankingOnFile} onChange={set('bankingOnFile')} disabled={!mutable}>
            <option value="">Not given</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
            <option value="unsure">Unsure</option>
          </select>
        </PField>
        <PField title="Dietary" wide>
          <input className={inputClass} value={form.dietary} onChange={set('dietary')} disabled={!mutable} />
        </PField>
        <PField title="Accessibility" wide>
          <input
            className={inputClass}
            value={form.accessibility}
            onChange={set('accessibility')}
            disabled={!mutable}
          />
        </PField>
        <PField title="Note" wide hint="Never banking details.">
          <textarea className={inputClass} rows={2} value={form.note} onChange={set('note')} disabled={!mutable} />
        </PField>
      </div>
      <StatusHistory inviteeId={invitee.id} />
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

const HISTORY_SOURCE: Record<string, string> = {
  operator: 'by hand',
  registration: 'registration form',
  eventbrite: 'Eventbrite',
  checkin: 'check-in link',
  import: 'imported list',
  automatic: 'automatic',
  walk_in: 'walk-in',
};
const stamp = (iso: string) =>
  new Date(iso).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

function StatusHistory({ inviteeId, compact = false }: { inviteeId: string; compact?: boolean }) {
  const q = useQuery<Invitee>(
    ['event-invitee', inviteeId],
    async () => (await api.get(`/events/invitees/${inviteeId}`)).data.data,
  );
  const rows = [...(q.data?.history ?? [])].reverse();
  const body = q.isLoading ? (
    <SkeletonRows rows={2} />
  ) : !rows.length ? (
    <p className="text-[15px] text-ink-2">No changes recorded.</p>
  ) : compact ? (
    <ul className="text-[15px] leading-[22px]">
      {rows.map((h, i) => (
        <li key={i} className="py-2 border-b border-line">
          <span className="text-ink">
            {h.from ? `${statusWord(h.from)} → ` : ''}
            {statusWord(h.to)}
          </span>
          <span className={cem}>
            {stamp(h.at)} · {HISTORY_SOURCE[h.source] ?? label(h.source)}
            {h.byEmail ? ` · ${h.byEmail}` : ''}
            {h.note ? ` · ${h.note}` : ''}
          </span>
        </li>
      ))}
    </ul>
  ) : (
    <div className="overflow-x-auto">
      <table className="w-full tabular">
        <thead>
          <tr className={cheadRow}>
            <th className={cth}>When</th>
            <th className={cth}>Change</th>
            <th className={cth}>How</th>
            <th className={cth}>Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h, i) => (
            <tr key={i} className="border-b border-line">
              <td className={`${ctd} whitespace-nowrap`}>{stamp(h.at)}</td>
              <td className={ctd}>
                {h.from ? `${statusWord(h.from)} → ` : ''}
                {statusWord(h.to)}
              </td>
              <td className={ctd}>
                {HISTORY_SOURCE[h.source] ?? label(h.source)}
                {h.byEmail ? <span className={cem}>{h.byEmail}</span> : null}
              </td>
              <td className={ctd}>{h.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  if (compact) return body;
  return (
    <div className="mt-6">
      <h3 className="text-[15px] leading-[22px] font-semibold text-ink mb-1">Status history</h3>
      {body}
    </div>
  );
}

function AddPeople({ event, onClose, onDone }: { event: EventView; onClose: () => void; onDone: () => void }) {
  const eventId = event.id;
  const [emails, setEmails] = useState('');
  const [audienceKey, setAudienceKey] = useState(
    event.audiences.find((a) => a.source.kind === 'list')?.key ?? event.audiences[0]?.key ?? '',
  );
  const [role, setRole] = useState('');
  const found = splitList(emails.replace(/[\s;]+/g, ','));
  const add = useMutation(
    async () =>
      (
        await api.post(`/events/${eventId}/invitees`, {
          people: found.map((email) => ({ email })),
          audienceKey,
          role: role || null,
        })
      ).data.data as { added: number; existing: number; errors: { index: number; message: string }[] },
    { onSuccess: onDone },
  );
  return (
    <Modal
      title="Add people"
      why={
        <>
          Paste one or more email addresses. Anyone without a member or contact record needs adding on{' '}
          <Link to="/contacts">Contacts</Link> first.
        </>
      }
      onClose={onClose}
      footer={
        add.data ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="primary" disabled={!found.length || add.isLoading} onClick={() => add.mutate()}>
              {add.isLoading
                ? 'Adding…'
                : found.length
                  ? `Add ${found.length} ${found.length === 1 ? 'person' : 'people'}`
                  : 'Add people'}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <PField
          title="Email addresses"
          hint={
            found.length
              ? `${found.length} address${found.length === 1 ? '' : 'es'} found`
              : 'One per line, or separated by commas.'
          }
        >
          <textarea
            id="add-people-emails"
            className={`${inputClass} resize-y`}
            rows={4}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
          />
        </PField>
        <div className={pGrid}>
          <PField title="Audience">
            <select className={inputClass} value={audienceKey} onChange={(e) => setAudienceKey(e.target.value)}>
              {event.audiences.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.name}
                </option>
              ))}
            </select>
          </PField>
          <PField title="Role (optional)">
            <input
              className={inputClass}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="For example, Speaker"
            />
          </PField>
        </div>
      </div>
      {add.data ? (
        <p className="text-[15px] text-ink mt-4">
          {add.data.added} added · {add.data.existing} already on the list
          {add.data.errors.length
            ? ` · ${add.data.errors.length} not added: ${add.data.errors.map((e) => e.message).join('; ')}`
            : ''}
        </p>
      ) : null}
      {add.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(add.error, 'Could not add.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function GiftCards({
  eventId,
  inviteeIds,
  onClose,
  onDone,
}: {
  eventId: string;
  inviteeIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('20');
  const [note, setNote] = useState('');
  const save = useMutation(
    async () =>
      (
        await api.post(`/events/${eventId}/gift-cards`, {
          inviteeIds,
          amountCents: toCents(amount),
          note: note || null,
        })
      ).data.data as { created: number; existing: number },
    { onSuccess: onDone },
  );
  return (
    <Modal
      title={`Gift cards for ${inviteeIds.length} ${inviteeIds.length === 1 ? 'winner' : 'winners'}`}
      why="One gift-card entry per person for this event in the payments ledger, owed until bought."
      onClose={onClose}
      footer={
        save.data ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
              Record the gift cards
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        )
      }
    >
      <div className={pGrid}>
        <PField title="Amount ($)">
          <input className={inputClass} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </PField>
        <PField title="Note">
          <input
            className={inputClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="For example, lunch activity"
          />
        </PField>
      </div>
      {save.data ? (
        <p className="text-[15px] text-ink mt-4">
          {save.data.created} recorded · {save.data.existing} already had one
        </p>
      ) : null}
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not record.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function ChangeStatus({
  invitee,
  statuses,
  onClose,
  onSaved,
}: {
  invitee: Invitee;
  statuses: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState(invitee.status);
  const save = useMutation(async () => api.patch(`/events/invitees/${invitee.id}`, { status }), {
    onSuccess: onSaved,
  });
  return (
    <Modal
      title={`Change ${firstName(invitee)}’s status`}
      why={`Now ${statusWord(invitee.status).toLowerCase()}. The change is kept in their status history.`}
      onClose={onClose}
      footer={
        <>
          <Button
            variant="primary"
            disabled={status === invitee.status || save.isLoading}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Saving…' : `Mark ${statusWord(status).toLowerCase()}`}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <PField title="Status">
        <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {statusWord(s)}
            </option>
          ))}
        </select>
      </PField>
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

const NO_ROLE = 'No role';
const PAGE = 50;
/** A follow-up that is a real conflict (a possible double payment) rather than ordinary work. */
const CONFLICT_RULES = new Set(['eventbrite_core']);

function hotelText(n: Needs, i: Invitee) {
  if (n.hotel === undefined || n.hotel === null) return null;
  if (!n.hotel) return 'Not needed';
  const nights = n.nights ?? i.eligibility?.nights;
  return `Requested${nights ? ` · ${nights} night${nights === 1 ? '' : 's'}` : ''}`;
}

function travelText(n: Needs) {
  const parts = [
    n.travelMode ? (TRAVEL_LABEL[n.travelMode] ?? n.travelMode) : null,
    n.travellingFrom ? `from ${n.travellingFrom}` : null,
    n.travelHours ? `${n.travelHours} h` : null,
    n.flightHours ? `${n.flightHours} h flight` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** The open person, beside the table (a full-screen view on a phone). Admins only, as the invitee detail is. */
function PersonPanel({
  invitee,
  detail,
  items,
  mutable,
  onClose,
  onEdit,
  onChangeStatus,
  onChanged,
}: {
  invitee: Invitee;
  detail: EventDetail;
  items: FollowupItem[];
  mutable: boolean;
  onClose: () => void;
  onEdit: () => void;
  onChangeStatus: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const full = useQuery<Invitee>(
    ['event-invitee', invitee.id],
    async () => (await api.get(`/events/invitees/${invitee.id}`)).data.data,
  );
  const i = full.data ?? invitee;
  const n = i.needs ?? {};
  const done = useMutation(
    async ({ key, state }: { key: string; state: 'done' | 'open' }) =>
      api.post(`/events/${detail.event.id}/followups/state`, { key, state }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('event-followups');
        queryClient.invalidateQueries('event-dashboard');
        onChanged();
      },
    },
  );
  const open = items.filter((f) => f.state === 'open');
  const closed = items.filter((f) => f.state !== 'open');
  const name = personName(i.person);
  const attending =
    n.attendanceMode === 'virtual' ? 'attending online' : n.attendanceMode === 'in_person' ? 'attending in person' : '';
  const days = n.attendDays?.length ? n.attendDays.map((d) => dayLabel(d).split(',')[0]).join(', ') : '';
  const flags = i.eligibility?.flags ?? [];
  const rows: [string, ReactNode, boolean?][] = [
    ['Role', i.role],
    ['Audience', audienceName(detail.event.audiences, i.audienceKey)],
    ['Affiliation', i.affiliation, true],
    ['Travel', travelText(n)],
    ['Hotel', hotelText(n, i)],
    ['Parking', n.parking === undefined ? null : n.parking ? 'Pass asked for' : 'Not needed'],
    ['Diet', n.dietary ?? <span className="text-ink-2">None given</span>],
    ['Accessibility', n.accessibility],
    ['Travel covered', i.eligibility ? (i.eligibility.funded ? 'Yes' : 'No') : null],
    ['Banking information on file', n.bankingOnFile ? label(n.bankingOnFile) : null],
    ['Registered through', cameFrom(i)],
    ['Added', (HISTORY_SOURCE[i.source] ?? label(i.source)).replace(/^./, (c) => c.toUpperCase())],
    ['Check-in code', i.checkinCode],
    ['Checked in', i.checkedIn.length ? i.checkedIn.map((d) => dayLabel(d).split(',')[0]).join(', ') : null],
    ['Note', i.note, true],
  ];
  const head = 'text-small font-semibold text-ink-2 mb-2';
  return (
    <aside
      aria-labelledby="person-name"
      className="flex-[1_1_360px] max-w-[420px] bg-surface border border-line rounded-md shadow-lift p-6 md:sticky md:top-[88px] md:max-h-[calc(100vh-104px)] md:overflow-y-auto motion-safe:animate-panel-in max-md:fixed max-md:inset-0 max-md:z-[55] max-md:max-w-none max-md:rounded-none max-md:border-0 max-md:shadow-none max-md:bg-paper max-md:overflow-y-auto max-md:overscroll-contain max-md:px-4 max-md:pt-[max(8px,env(safe-area-inset-top))] max-md:pb-12 max-md:motion-safe:animate-sheet-in"
    >
      <button
        type="button"
        onClick={onClose}
        className="md:hidden inline-flex items-center gap-[6px] min-h-[44px] text-[15px] leading-5 font-semibold text-ink mb-4"
      >
        <svg
          aria-hidden
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
        >
          <path d="M10 3L5 8l5 5" />
        </svg>
        People
      </button>
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <h2 id="person-name" className="font-serif font-semibold text-[24px] leading-8 text-ink">
            {name}
          </h2>
          {i.person.email ? (
            <p className="text-[15px] leading-[22px] mt-[2px] [overflow-wrap:anywhere]">
              <a href={`mailto:${i.person.email}`}>{i.person.email}</a>
            </p>
          ) : (
            <p className="text-[15px] text-ink-3 mt-[2px]">No email</p>
          )}
        </div>
        <Button
          variant="icon"
          className="border-transparent max-md:hidden"
          aria-label={`Close ${name}’s details`}
          onClick={onClose}
        >
          <svg
            aria-hidden
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
          >
            <path d="M3 3l10 10M13 3L3 13" />
          </svg>
        </Button>
      </div>
      <p className="mt-4 text-[15px] leading-[22px] text-ink flex flex-wrap items-center gap-x-1">
        <StatusTag status={i.status} />
        {attending ? <span>· {attending}</span> : null}
        {days ? <span className="text-ink-2">· {days}</span> : null}
      </p>
      {open.length || closed.length ? (
        <section className="mt-5 pt-4 border-t border-line">
          <h3 className={head}>
            {open.length === 1 ? 'Open follow-up' : open.length ? 'Open follow-ups' : 'Follow-ups'}
          </h3>
          {open.map((f) => (
            <div key={f.key} className="flex justify-between gap-3 items-start mb-3">
              <p className="text-[15px] leading-[22px] text-ink min-w-0">
                {CONFLICT_RULES.has(f.rule) ? (
                  <span className="inline-flex gap-[6px] items-center text-warning font-semibold">
                    <WarningIcon />
                    {f.title}
                  </span>
                ) : (
                  f.title
                )}
                <span className={cem}>{f.message}</span>
              </p>
              {mutable ? (
                <Button size="sm" disabled={done.isLoading} onClick={() => done.mutate({ key: f.key, state: 'done' })}>
                  Done
                </Button>
              ) : null}
            </div>
          ))}
          {closed.map((f) => (
            <div key={f.key} className="flex justify-between gap-3 items-center text-[15px] text-ink-2 mb-1">
              <span className="inline-flex gap-[6px] items-center">
                <Pill tone="done">{`${f.title}: ${f.state === 'done' ? 'done' : 'dismissed'}`}</Pill>
              </span>
              {mutable ? (
                <Button
                  size="sm"
                  variant="quiet"
                  disabled={done.isLoading}
                  onClick={() => done.mutate({ key: f.key, state: 'open' })}
                >
                  Undo
                </Button>
              ) : null}
            </div>
          ))}
          {done.error ? <ErrorNotice>{errorMessage(done.error, 'Could not save.')}</ErrorNotice> : null}
        </section>
      ) : null}
      {flags.length && !items.some((f) => f.rule === 'eligibility') ? (
        <section className="mt-5 pt-4 border-t border-line">
          <h3 className={head}>Travel rules</h3>
          <ul className="text-[15px] leading-[22px] text-ink">
            {flags.map((f) => (
              <li key={f.rule}>
                {f.severity === 'fail' ? 'Outside the rules: ' : 'To check: '}
                {f.message}
              </li>
            ))}
          </ul>
          {i.eligibility ? (
            <p className={cem}>
              Hotel {i.eligibility.nights} night{i.eligibility.nights === 1 ? '' : 's'}
              {i.eligibility.extraNight ? ', including an extra night' : ''}
            </p>
          ) : null}
        </section>
      ) : null}
      <dl className="mt-5 pt-4 border-t border-line grid grid-cols-2 gap-x-5 gap-y-4 max-md:grid-cols-1 max-md:gap-0">
        {rows
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v, wide]) => (
            <div
              key={k}
              className={`${wide ? 'col-span-2 max-md:col-span-1' : ''} max-md:flex max-md:justify-between max-md:gap-4 max-md:py-[11px] max-md:border-b max-md:border-line`}
            >
              <dt className="text-small text-ink-3 max-md:text-[15px] max-md:flex-none">{k}</dt>
              <dd className="mt-[2px] text-[15px] leading-[22px] text-ink max-md:mt-0 max-md:text-right [overflow-wrap:anywhere]">
                {v}
              </dd>
            </div>
          ))}
      </dl>
      <p className="mt-5 text-[15px] leading-[22px]">
        <button type="button" className="text-accent underline underline-offset-[3px] hover:text-ink" onClick={onEdit}>
          See all form answers
        </button>
      </p>
      <details className="mt-3 text-[15px] leading-[22px]">
        <summary className="cursor-pointer text-accent hover:text-ink w-fit list-none [&::-webkit-details-marker]:hidden">
          Status history
        </summary>
        <div className="mt-2">
          <StatusHistory inviteeId={i.id} compact />
        </div>
      </details>
      <div className="mt-6 flex flex-wrap gap-2 max-md:flex-col">
        {mutable ? <Button onClick={onChangeStatus}>Change status</Button> : null}
        {i.person.email ? (
          <a
            href={`mailto:${i.person.email}`}
            className="inline-flex items-center justify-center min-h-[44px] px-4 py-[11px] rounded-sm border border-line-strong text-ink font-semibold text-[15px] leading-5 no-underline hover:bg-selected hover:text-ink"
          >
            Email {firstName(i)}
          </a>
        ) : null}
        <MoreMenu
          label={`More for ${name}`}
          items={[
            { label: mutable ? 'Edit details' : 'See all form answers', onClick: onEdit },
            ...(i.person.type === 'member' && i.person.id
              ? [
                  {
                    label: 'Open the member record',
                    onClick: () => navigate(`/analytics/members/${i.person.id}`),
                  },
                ]
              : []),
          ]}
        />
      </div>
    </aside>
  );
}

function Invitees({ detail, mutable, refresh }: PanelProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [audience, setAudience] = useState('');
  const [q, setQ] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [channel, setChannel] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [moreFilters, setMoreFilters] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [picked, setPicked] = useState<string[]>([]);
  const [open, setOpen] = useState<Invitee | null>(null);
  const [modal, setModal] = useState<'add' | 'gift' | 'edit' | 'status' | null>(null);
  const [bulk, setBulk] = useState('confirmed');
  const [day, setDay] = useState(
    detail.event.days.find((d) => d.date === torontoToday())?.date ?? detail.event.days[0].date,
  );
  const list = useQuery<{
    invitees: Invitee[];
    needsVisible: boolean;
    total: number;
    countsOnly?: boolean;
    counts?: InviteeCounts;
  }>(
    ['event-invitees', detail.event.id, status, audience],
    async () =>
      (
        await api.get(`/events/${detail.event.id}/invitees`, {
          params: { status: status || undefined, audience: audience || undefined },
        })
      ).data.data,
  );
  const followups = useQuery<Followups>(
    ['event-followups', detail.event.id, 'people'],
    async () => (await api.get(`/events/${detail.event.id}/followups`)).data.data,
  );
  const byInvitee = useMemo(() => {
    const m = new Map<string, FollowupItem[]>();
    for (const f of followups.data?.items ?? [])
      if (f.inviteeId) m.set(f.inviteeId, [...(m.get(f.inviteeId) ?? []), f]);
    return m;
  }, [followups.data]);
  const attention = useMemo(() => {
    const m = new Map<string, FollowupItem[]>();
    for (const [id, items] of byInvitee) {
      const live = items.filter((f) => f.state === 'open' && f.severity === 'action');
      if (live.length) m.set(id, live);
    }
    return m;
  }, [byInvitee]);
  const done = () => {
    queryClient.invalidateQueries('event-invitees');
    queryClient.invalidateQueries('event-invitee');
    refresh();
  };
  const bulkStatus = useMutation(
    async () => api.post(`/events/${detail.event.id}/invitees/status`, { inviteeIds: picked, status: bulk }),
    {
      onSuccess: (res) => {
        setPicked([]);
        done();
        const notStarted = Number(res.data?.data?.notStarted || 0);
        if (notStarted)
          void confirmAction(
            `${notStarted} ${notStarted === 1 ? 'person was' : 'people were'} not marked attended: none of their event days has started. Check them in on the day instead.`,
            'OK',
          );
      },
    },
  );
  const checkin = useMutation(
    async (undo: boolean) => api.post(`/events/${detail.event.id}/checkins`, { inviteeIds: picked, day, undo }),
    {
      onSuccess: () => {
        setPicked([]);
        done();
      },
    },
  );
  const filtered = useMemo(
    () =>
      (list.data?.invitees ?? []).filter(
        (i) =>
          (!channel || cameFrom(i) === channel) &&
          (!roleFilter || (i.role ?? NO_ROLE) === roleFilter) &&
          (!flagged || attention.has(i.id) || Boolean(i.eligibility?.flags?.length)),
      ),
    [list.data, channel, roleFilter, flagged, attention],
  );
  const needle = q.trim().toLowerCase();
  const rows = needle
    ? filtered.filter((i) =>
        `${i.person.name ?? ''} ${i.person.email ?? ''} ${i.affiliation ?? ''}`.toLowerCase().includes(needle),
      )
    : filtered;
  const roles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of list.data?.invitees ?? []) counts.set(i.role ?? NO_ROLE, (counts.get(i.role ?? NO_ROLE) ?? 0) + 1);
    return [...counts.entries()].sort(([a], [b]) => (a === NO_ROLE ? 1 : b === NO_ROLE ? -1 : a.localeCompare(b)));
  }, [list.data]);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const filter =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setLimit(PAGE);
    };
  const needs = list.data?.needsVisible;
  const shown = rows.slice(0, limit);
  const all = list.data?.invitees.length ?? 0;
  const started = detail.event.days[0]?.date <= torontoToday();
  const showCheckedIn = started || rows.some((i) => i.checkedIn.length);
  const extraFilters = Number(Boolean(audience)) + Number(Boolean(channel)) + Number(flagged);

  const by = detail.byStatus;
  const onList = Object.values(by).reduce((a, b) => a + b, 0);
  const coming = (by.registered ?? 0) + (by.confirmed ?? 0) + (by.attended ?? 0);
  const others = [
    by.waitlisted ? `${fmt(by.waitlisted)} ${by.waitlisted === 1 ? 'is' : 'are'} waitlisted` : null,
    by.no_show ? `${fmt(by.no_show)} didn’t come` : null,
  ].filter(Boolean);
  const lead = onList
    ? `${fmt(onList)} ${onList === 1 ? 'person is' : 'people are'} invited. ${fmt(coming)} ${
        coming === 1 ? 'is' : 'are'
      } coming, ${fmt(by.invited ?? 0)} haven’t answered${others.length ? `, ${others.join(', ')}` : ''} and ${fmt(
        by.declined ?? 0,
      )} declined.`
    : 'Nobody is invited yet.';

  const followupCell = (i: Invitee) => {
    const items = attention.get(i.id) ?? [];
    const conflicts = items.filter((f) => CONFLICT_RULES.has(f.rule));
    const plain = items.length - conflicts.length;
    return (
      <>
        {conflicts.map((f) => (
          <span key={f.key} className="flex gap-[6px] items-center text-small text-warning font-semibold">
            <WarningIcon />
            Also bought a ticket: check refund
          </span>
        ))}
        {plain ? (
          <button
            type="button"
            className="block text-small text-accent underline underline-offset-[3px] hover:text-ink"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(i);
            }}
          >
            {plain} follow-up{plain === 1 ? '' : 's'}
          </button>
        ) : null}
      </>
    );
  };

  const selectLabel = 'inline-flex items-center gap-2 text-[15px] leading-5 text-ink-2';
  const toolSelect = 'border border-line-strong rounded-sm bg-white px-[10px] py-2 min-h-[40px] text-[15px] text-ink';

  return (
    <>
      <Lead lead={lead} sub="Everyone invited to this event. Open a name to see their answers and follow-ups.">
        {mutable ? (
          <Button variant="primary" onClick={() => setModal('add')}>
            Add people
          </Button>
        ) : null}
      </Lead>
      <AudiencesPanel eventId={detail.event.id} canEdit={mutable} onChange={refresh} />
      {list.data?.countsOnly && list.data.counts ? (
        <Panel className="mt-8">
          <PanelHeader title="Invitees" note={`${fmt(list.data.total)} people`} />
          <InviteeCountsView counts={list.data.counts} audiences={detail.event.audiences} />
        </Panel>
      ) : (
        <div className="flex flex-wrap gap-8 mt-8 items-start">
          <div className="flex-[999_1_640px] min-w-0">
            <div className="flex flex-wrap gap-3 items-center pb-3">
              <label htmlFor="people-search" className="sr-only">
                Search people
              </label>
              <input
                id="people-search"
                type="search"
                className="flex-[1_1_240px] min-h-[40px] border border-line-strong rounded-sm bg-white px-3 py-2 text-[15px] text-ink"
                placeholder="Search name, email or affiliation"
                value={q}
                onChange={(e) => filter(setQ)(e.target.value)}
              />
              <label className={selectLabel}>
                Status
                <select className={toolSelect} value={status} onChange={(e) => filter(setStatus)(e.target.value)}>
                  <option value="">Everyone</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {statusWord(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className={selectLabel}>
                Role
                <select
                  className={toolSelect}
                  value={roleFilter}
                  onChange={(e) => filter(setRoleFilter)(e.target.value)}
                >
                  <option value="">Any role</option>
                  {roles.map(([r, n]) => (
                    <option key={r} value={r}>
                      {r} ({n})
                    </option>
                  ))}
                </select>
              </label>
              <Button
                aria-expanded={moreFilters}
                aria-controls="people-more-filters"
                className="min-h-[40px] py-[9px]"
                onClick={() => setMoreFilters((v) => !v)}
              >
                {extraFilters ? `More filters · ${extraFilters}` : 'More filters'}
              </Button>
              <span className="ml-auto text-small text-ink-3">
                {rows.length === all ? `Showing all ${fmt(all)}` : `Showing ${fmt(rows.length)} of ${fmt(all)}`}
              </span>
            </div>
            {moreFilters ? (
              <div id="people-more-filters" className="flex flex-wrap gap-x-6 gap-y-3 items-center pb-4">
                <label className={selectLabel}>
                  Audience
                  <select className={toolSelect} value={audience} onChange={(e) => filter(setAudience)(e.target.value)}>
                    <option value="">Every audience</option>
                    {detail.event.audiences.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={selectLabel}>
                  Way of registering
                  <select className={toolSelect} value={channel} onChange={(e) => filter(setChannel)(e.target.value)}>
                    <option value="">Any way</option>
                    {['Confirmation form', 'Eventbrite', 'Added by staff', 'No response yet'].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
                {needs ? (
                  <label className="inline-flex items-center gap-2 text-[15px] text-ink min-h-[40px]">
                    <input
                      type="checkbox"
                      className="w-[18px] h-[18px] accent-accent"
                      checked={flagged}
                      onChange={(e) => filter(setFlagged)(e.target.checked)}
                    />
                    Has follow-ups
                  </label>
                ) : null}
                {extraFilters ? (
                  <Button
                    variant="quiet"
                    size="sm"
                    onClick={() => {
                      setAudience('');
                      setChannel('');
                      setFlagged(false);
                    }}
                  >
                    Clear these filters
                  </Button>
                ) : null}
              </div>
            ) : null}
            {!needs && list.data ? (
              <div className="mb-3">
                <InfoNotice>Needs (dietary, accessibility, hotel and travel) are shown to admins only.</InfoNotice>
              </div>
            ) : null}
            {mutable && picked.length ? (
              <div className="flex flex-wrap gap-x-3 gap-y-2 items-center mb-3 px-3 py-2 rounded-sm bg-primary-tint text-[15px] text-ink">
                <span className="font-semibold">{picked.length} selected</span>
                <select
                  aria-label="New status"
                  className={toolSelect}
                  value={bulk}
                  onChange={(e) => setBulk(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {statusWord(s)}
                    </option>
                  ))}
                </select>
                <Button size="sm" disabled={bulkStatus.isLoading} onClick={() => bulkStatus.mutate()}>
                  Set status
                </Button>
                <span className="text-ink-3 max-md:hidden">·</span>
                <select
                  aria-label="Check-in day"
                  className={toolSelect}
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                >
                  {detail.event.days.map((d) => (
                    <option key={d.date} value={d.date}>
                      {dayLabel(d.date)}
                    </option>
                  ))}
                </select>
                <Button size="sm" disabled={checkin.isLoading} onClick={() => checkin.mutate(false)}>
                  Check in
                </Button>
                <Button size="sm" variant="quiet" disabled={checkin.isLoading} onClick={() => checkin.mutate(true)}>
                  Undo check-in
                </Button>
                <Button size="sm" variant="quiet" onClick={() => setModal('gift')}>
                  Gift cards
                </Button>
                <Button size="sm" variant="quiet" onClick={() => setPicked([])}>
                  Clear
                </Button>
              </div>
            ) : null}
            {bulkStatus.error || checkin.error ? (
              <div className="mb-3">
                <ErrorNotice>{errorMessage(bulkStatus.error || checkin.error, 'Could not save.')}</ErrorNotice>
              </div>
            ) : null}
            {list.isLoading ? (
              <SkeletonRows rows={6} />
            ) : list.error ? (
              <ErrorNotice>Could not load the invitees.</ErrorNotice>
            ) : !rows.length ? (
              needle ? (
                <EmptyState title={`No one matches “${q.trim()}”.`}>
                  <button
                    type="button"
                    className="text-accent underline underline-offset-[3px] hover:text-ink"
                    onClick={() => setQ('')}
                  >
                    Clear the search
                  </button>{' '}
                  to see all {fmt(filtered.length)} people.
                </EmptyState>
              ) : all || status || audience ? (
                <EmptyState title="No one matches these filters.">Change the filters to see more people.</EmptyState>
              ) : (
                <EmptyState title="Nobody on the list yet.">
                  Fill an audience from the platform, add people, or link the registration form on the Overview tab.
                </EmptyState>
              )
            ) : (
              <>
                <div className="md:overflow-x-auto">
                  <table className="w-full tabular md:min-w-[720px] max-md:block">
                    <thead className="max-md:hidden">
                      <tr className={cheadRow}>
                        {mutable ? (
                          <th className={`${cth} w-[30px]`}>
                            <input
                              type="checkbox"
                              className="w-[18px] h-[18px] accent-accent"
                              aria-label={`Select all ${rows.length}`}
                              checked={picked.length > 0 && picked.length === rows.length}
                              onChange={(e) => setPicked(e.target.checked ? rows.map((r) => r.id) : [])}
                            />
                          </th>
                        ) : null}
                        <th className={cth}>Name</th>
                        <th className={cth}>Role</th>
                        <th className={cth}>Status</th>
                        <th className={cth}>Affiliation</th>
                        {needs ? <th className={cth}>Needs</th> : null}
                        {showCheckedIn ? <th className={cth}>Checked in</th> : null}
                      </tr>
                    </thead>
                    <tbody className="max-md:block">
                      {shown.map((i) => {
                        const flags = i.eligibility?.flags ?? [];
                        const isOpen = open?.id === i.id;
                        const name = personName(i.person);
                        const roleText = [
                          i.role,
                          i.audienceKey ? audienceName(detail.event.audiences, i.audienceKey) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <tr
                            key={i.id}
                            aria-selected={isOpen || undefined}
                            className={`${stackRow} ${
                              isOpen
                                ? 'bg-accent-tint'
                                : picked.includes(i.id)
                                  ? 'bg-primary-tint'
                                  : 'hover:bg-selected'
                            } ${needs ? 'cursor-pointer' : ''} max-md:relative max-md:pl-8`}
                            onClick={needs ? () => setOpen(i) : undefined}
                          >
                            {mutable ? (
                              <td
                                className={`${ctd} max-md:absolute max-md:left-0 max-md:top-3 max-md:p-0`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="w-[18px] h-[18px] accent-accent mt-[2px]"
                                  aria-label={`Select ${name}`}
                                  checked={picked.includes(i.id)}
                                  onChange={() => toggle(i.id)}
                                />
                              </td>
                            ) : null}
                            <td className={`${ctd} max-md:block max-md:py-0 max-md:pr-0`}>
                              <span className="max-md:flex max-md:justify-between max-md:items-baseline max-md:gap-3">
                                {needs ? (
                                  <button
                                    type="button"
                                    aria-current={isOpen || undefined}
                                    className="text-left font-semibold text-ink hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpen(i);
                                    }}
                                  >
                                    {name}
                                  </button>
                                ) : (
                                  <span className="font-semibold">{name}</span>
                                )}
                                <span className="md:hidden">
                                  <StatusTag status={i.status} small />
                                </span>
                              </span>
                              <span className={`${cem} max-md:[overflow-wrap:anywhere]`}>
                                {i.person.email ?? (i.person.type === 'contact' ? 'Contact, no email' : '')}
                              </span>
                            </td>
                            <td className={stackCell} data-label="Role">
                              {roleText || <span className="text-ink-3">–</span>}
                            </td>
                            <td className={`${ctd} max-md:hidden`}>
                              <StatusTag status={i.status} />
                              {i.invitedAt && i.status === 'invited' ? (
                                <span className={cem}>Invitation sent</span>
                              ) : null}
                            </td>
                            <td className={stackCell} data-label="Affiliation">
                              {i.affiliation ?? ''}
                            </td>
                            {needs ? (
                              <td className={stackCell} data-label="Needs">
                                {needsSummary(i.needs) || null}
                                {flags.length && !byInvitee.get(i.id)?.some((f) => f.rule === 'eligibility') ? (
                                  <span className={cem}>
                                    {flags.some((f) => f.severity === 'fail') ? 'Outside the travel rules' : 'To check'}
                                    : {flags[0].message}
                                    {flags.length > 1 ? ` (and ${flags.length - 1} more)` : ''}
                                  </span>
                                ) : null}
                                {followupCell(i)}
                              </td>
                            ) : null}
                            {showCheckedIn ? (
                              <td className={stackCell} data-label="Checked in">
                                {i.checkedIn.map((d) => dayLabel(d).split(',')[0]).join(', ') || (
                                  <span className="text-ink-3">–</span>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {rows.length > shown.length ? (
                  <p className="mt-3 text-[15px] leading-[22px]">
                    <button
                      type="button"
                      className="text-accent underline underline-offset-[3px] hover:text-ink"
                      onClick={() => setLimit((l) => l + PAGE)}
                    >
                      Show the next {Math.min(PAGE, rows.length - shown.length)}
                    </button>{' '}
                    <span className="text-ink-3">
                      · {fmt(shown.length)} of {fmt(rows.length)} shown
                    </span>
                  </p>
                ) : null}
              </>
            )}
          </div>
          {open && needs ? (
            <PersonPanel
              key={open.id}
              invitee={open}
              detail={detail}
              items={byInvitee.get(open.id) ?? []}
              mutable={mutable}
              onClose={() => setOpen(null)}
              onEdit={() => setModal('edit')}
              onChangeStatus={() => setModal('status')}
              onChanged={done}
            />
          ) : null}
        </div>
      )}
      {open && modal === 'edit' ? (
        <InviteeModal
          invitee={open}
          detail={detail}
          mutable={mutable}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            done();
          }}
        />
      ) : null}
      {open && modal === 'status' ? (
        <ChangeStatus
          invitee={open}
          statuses={detail.statuses}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            done();
          }}
        />
      ) : null}
      {modal === 'add' ? <AddPeople event={detail.event} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal === 'gift' ? (
        <GiftCards eventId={detail.event.id} inviteeIds={picked} onClose={() => setModal(null)} onDone={done} />
      ) : null}
    </>
  );
}

interface InviteeCounts {
  byStatus: Record<string, number>;
  byAudience: Record<string, number>;
}

function InviteeCountsView({ counts, audiences }: { counts: InviteeCounts; audiences: EventView['audiences'] }) {
  return (
    <>
      <InfoNotice>Your account sees counts. Names, emails, affiliations and needs are shown to admins only.</InfoNotice>
      <div className="grid grid-cols-2 gap-12 mt-6 max-md:grid-cols-1 max-md:gap-8">
        <table className="w-full tabular">
          <thead>
            <tr className={cheadRow}>
              <th className={cth}>Status</th>
              <th className={`${cth} ${cnum}`}>People</th>
            </tr>
          </thead>
          <tbody>
            {STATUSES.filter((s) => counts.byStatus[s]).map((s) => (
              <tr key={s} className="border-b border-line">
                <td className={ctd}>
                  <StatusTag status={s} />
                </td>
                <td className={`${ctd} ${cnum}`}>{fmt(counts.byStatus[s])}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="w-full tabular">
          <thead>
            <tr className={cheadRow}>
              <th className={cth}>Audience</th>
              <th className={`${cth} ${cnum}`}>People</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(counts.byAudience).map(([k, n]) => (
              <tr key={k} className="border-b border-line">
                <td className={ctd}>{k === 'none' ? 'No audience' : audienceName(audiences, k)}</td>
                <td className={`${ctd} ${cnum}`}>{fmt(n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- invitations and messages

interface Preview {
  templateKey: string;
  kind: 'bulk' | 'transactional';
  recipients: { inviteeId: string; name: string; email: string | null; ok: boolean; reason: string | null }[];
  missing: string[];
  outbox: boolean;
  rendered: { subject: string; text: string } | null;
}

/** The platform's own messages besides the invitations, in the order they go out. */
const OTHER_MESSAGES: { kind: string; to: string; what: string; why: string }[] = [
  {
    kind: 'confirmation',
    to: 'Confirmed invitees who haven’t had it',
    what: 'Schedule, hotel nights, travel and reimbursement',
    why: 'Program email (transactional), to confirmed invitees who haven’t had it: the notes for attendees with the schedule, hotel nights, travel and reimbursement.',
  },
  {
    kind: 'reminder',
    to: 'Registered and confirmed invitees',
    what: 'A few days before',
    why: 'Program email (transactional), a few days before, to registered and confirmed invitees.',
  },
  {
    kind: 'evaluation',
    to: 'Everyone who attended',
    what: 'Thank-you with the survey link',
    why: 'Program email (transactional): a thank-you with the evaluation survey link, to everyone who attended.',
  },
];

function MessageModal({
  event,
  kind,
  audience,
  onClose,
  onSent,
}: {
  event: EventView;
  kind: string;
  audience?: Audience;
  onClose: () => void;
  onSent: () => void;
}) {
  const [vars, setVars] = useState({
    responseBy: '',
    responseByFr: '',
    formLink: event.formLink ?? '',
    details: '',
    detailsFr: '',
    evaluationLink: event.evaluationUrl ?? '',
  });
  const body = { kind, audience: audience?.key, vars: Object.fromEntries(Object.entries(vars).filter(([, v]) => v)) };
  const preview = useMutation(
    async () => (await api.post(`/events/${event.id}/messages/preview`, body)).data.data as Preview,
  );
  const send = useMutation(
    async () =>
      (await api.post(`/events/${event.id}/messages/send`, { ...body, confirm: true })).data.data as {
        sent: number;
        notSent: number;
        skipped: unknown[];
        statuses: Record<string, number>;
      },
    { onSuccess: onSent },
  );
  const edit = (k: keyof typeof vars) => (e: ChangeEvent<HTMLInputElement>) => {
    setVars({ ...vars, [k]: e.target.value });
    preview.reset();
  };
  const ok = preview.data?.recipients.filter((r) => r.ok) ?? [];
  const skipped = preview.data?.recipients.filter((r) => !r.ok) ?? [];
  const ready = preview.data && !preview.data.missing.length && ok.length > 0;
  const title = kind === 'invitation' ? `Invitations: ${audience?.name ?? ''}` : `${label(kind)} email`;
  const why =
    kind === 'invitation'
      ? audience?.funded
        ? `Program email (transactional) to “${audience.name}”: the invitation says travel is covered and asks them to confirm through the form.`
        : `Bulk email to “${audience?.name ?? ''}”: a registration invitation; unsubscribed addresses are skipped.`
      : (OTHER_MESSAGES.find((m) => m.kind === kind)?.why ?? `${label(kind)} email.`);
  return (
    <Modal
      title={title}
      why={<>{why} Nothing is sent until you press Send; people who already have it are skipped.</>}
      onClose={onClose}
      width={820}
      footer={
        send.data ? (
          <Button onClick={onClose}>Close</Button>
        ) : preview.data ? (
          <>
            <Button variant="primary" disabled={!ready || send.isLoading} onClick={() => send.mutate()}>
              {send.isLoading ? 'Sending…' : `Send to ${ok.length}`}
            </Button>
            <Button onClick={() => preview.mutate()} disabled={preview.isLoading}>
              Preview again
            </Button>
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={() => preview.mutate()} disabled={preview.isLoading}>
              {preview.isLoading ? 'Preparing…' : 'Preview'}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        )
      }
    >
      {kind === 'invitation' || kind === 'reminder' || kind === 'evaluation' ? (
        <div className={`${pGrid} mb-5`}>
          {kind === 'invitation' ? (
            <>
              <PField title="Reply by (English)">
                <input
                  className={inputClass}
                  value={vars.responseBy}
                  onChange={edit('responseBy')}
                  placeholder="April 15, 2027"
                />
              </PField>
              <PField title="Reply by (French)">
                <input
                  className={inputClass}
                  value={vars.responseByFr}
                  onChange={edit('responseByFr')}
                  placeholder="15 avril 2027"
                />
              </PField>
              <PField title="Form link" wide>
                <input className={inputClass} value={vars.formLink} onChange={edit('formLink')} />
              </PField>
            </>
          ) : null}
          {kind === 'reminder' ? (
            <>
              <PField title="Details (English)" wide>
                <input
                  className={inputClass}
                  value={vars.details}
                  onChange={edit('details')}
                  placeholder="Lunch is provided. Parking passes at the desk."
                />
              </PField>
              <PField title="Details (French)" wide>
                <input className={inputClass} value={vars.detailsFr} onChange={edit('detailsFr')} />
              </PField>
            </>
          ) : null}
          {kind === 'evaluation' ? (
            <PField title="Evaluation link" wide>
              <input className={inputClass} value={vars.evaluationLink} onChange={edit('evaluationLink')} />
            </PField>
          ) : null}
        </div>
      ) : null}
      {!preview.data && !preview.error ? (
        <p className="text-ink-2">Preview first to see who gets it and exactly what it says.</p>
      ) : null}
      {preview.error ? (
        <ErrorNotice>{errorMessage(preview.error, 'Could not prepare the message.')}</ErrorNotice>
      ) : null}
      {preview.data ? (
        <div className="flex flex-col gap-4">
          {preview.data.outbox ? (
            <InfoNotice>This copy of the platform is in outbox mode: messages are logged, not delivered.</InfoNotice>
          ) : null}
          {preview.data.missing.length ? (
            <ErrorNotice>Fill these in first: {preview.data.missing.join(', ')}.</ErrorNotice>
          ) : null}
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-6 max-md:grid-cols-1">
            <div>
              <p className="text-ink">
                <strong className="font-semibold">{ok.length} will receive it</strong> · {skipped.length} skipped
              </p>
              {skipped.length ? (
                <ul className="text-small text-ink-3 max-h-[220px] overflow-auto mt-1">
                  {skipped.map((r) => (
                    <li key={r.inviteeId}>
                      {r.name}: {r.reason}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {preview.data.rendered ? (
              <div className="border-l border-line pl-6 max-md:border-l-0 max-md:pl-0 max-md:border-t max-md:pt-4">
                <p className="text-small text-ink-3">Subject</p>
                <p className="font-semibold text-ink mb-3">{preview.data.rendered.subject}</p>
                <div className="whitespace-pre-wrap text-ink max-h-[320px] overflow-auto">
                  {preview.data.rendered.text}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {send.data ? (
        <p className="text-ink mt-4 font-semibold">
          Sent {send.data.sent} · not sent {send.data.notSent} · skipped {send.data.skipped.length}
        </p>
      ) : null}
      {send.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(send.error, 'Could not send.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function Invitations({ detail, mutable, refresh }: PanelProps) {
  const [message, setMessage] = useState<{ kind: string; audience?: Audience } | null>(null);
  const [outside, setOutside] = useState<Audience | null>(null);
  const comms = useEventComms(detail.event.id);
  const today = torontoToday();
  const audiences = detail.event.audiences;
  const clear = useMutation(
    async (a: Audience) =>
      (await api.patch(`/events/${detail.event.id}/audiences/${a.key}`, { invitedOutside: null })).data,
    { onSuccess: refresh },
  );
  const over = new Date(detail.event.endsAt) < new Date();
  // The next invitation to prepare: an audience not invited yet whose opening date has come (or has none), the
  // core audiences before the public one.
  const open = audiences.filter((a) => (!a.opensOn || a.opensOn <= today) && !a.sentAt && !a.invitedOutside);
  const due = open.find((a) => a.source.kind !== 'public') ?? open[0];
  const sentTo = audiences.filter((a) => a.sentAt).length;
  // The opening says what to do next: the platform's next email first, then the PM's own plan.
  const platform = due
    ? `The ${due.name} invitation is ready to prepare`
    : !sentTo
      ? 'No invitation has gone out from the platform yet'
      : sentTo === audiences.length
        ? `Invitations went out from the platform to ${sentTo === 1 ? 'the audience' : `all ${sentTo} audiences`}`
        : `Invitations went out from the platform to ${sentTo} of ${audiences.length} audiences`;
  const c = comms.data?.counts;
  const plan = !c
    ? ''
    : !c.planned
      ? ' Your own plan of emails is empty.'
      : c.overdue
        ? ` In your own plan, ${fmt(c.overdue)} ${c.overdue === 1 ? 'message is' : 'messages are'} past ${
            c.overdue === 1 ? 'its' : 'their'
          } date and not marked sent.`
        : c.sent === c.planned
          ? ` Every email in your own plan is marked sent.`
          : ` In your own plan, ${fmt(c.planned - c.sent)} of ${fmt(c.planned)} emails are still to send, none late.`;
  const act = 'whitespace-nowrap text-right max-md:text-left max-md:pt-2';
  return (
    <>
      <Lead
        lead={`${platform}.${plan}`}
        sub="Two kinds of email. First, the emails the platform sends for you. Below them, the emails you send yourself from your own mailbox, with their dates."
      >
        {mutable && due ? (
          <Button variant="primary" onClick={() => setMessage({ kind: 'invitation', audience: due })}>
            Prepare the {due.name} invitation
          </Button>
        ) : mutable && !over ? (
          <Button variant="primary" onClick={() => setMessage({ kind: 'confirmation' })}>
            Prepare the confirmation
          </Button>
        ) : null}
      </Lead>
      <Panel className="mt-12">
        <PanelHeader title="Emails the platform sends for you" />
        <PanelBody>
          <p className="text-[15px] leading-[22px] text-ink-2 mb-3 max-w-[72ch]">
            Press Prepare, check the preview, then send: nothing goes out until you press Send. Already invited an
            audience some other way? Use &ldquo;Invited elsewhere&rdquo; so nobody is invited twice.
          </p>
          <table className="w-full tabular max-md:block">
            <thead className="max-md:hidden">
              <tr className={cheadRow}>
                <th className={cth}>Message</th>
                <th className={cth}>To</th>
                <th className={`${cth} ${cnum}`}>On the list</th>
                <th className={cth}>Sent</th>
                {mutable ? (
                  <th className={cth}>
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="max-md:block">
              {audiences.map((a) => {
                const late = a.opensOn && a.opensOn <= today && !a.sentAt && !a.invitedOutside;
                return (
                  <tr key={a.key} className={stackRow}>
                    <td className={`${ctd} font-semibold max-md:block max-md:p-0`}>Invitation</td>
                    <td className={stackCell} data-label="To">
                      {a.name}
                      <span className={cem}>
                        {SOURCE_LABEL[a.source.kind]}
                        {a.funded ? ' · travel covered' : ''}
                      </span>
                    </td>
                    <td className={`${stackCell} md:text-right`} data-label="On the list">
                      {fmt(detail.byAudience[a.key] ?? 0)}
                    </td>
                    <td className={stackCell} data-label="Sent">
                      {a.sentAt ? (
                        `${dayLabel(a.sentAt.slice(0, 10))} · ${fmt(a.sentCount)}`
                      ) : a.invitedOutside ? (
                        <>
                          Outside the platform
                          <span className={cem}>
                            {a.invitedOutside.via}, {dayLabel(a.invitedOutside.on)}
                          </span>
                        </>
                      ) : (
                        <span className="text-ink-2">Not yet</span>
                      )}
                      {a.opensOn && !a.sentAt && !a.invitedOutside ? (
                        <span className={`${cem} ${late ? 'font-semibold text-ink' : ''}`}>
                          Opens {dayLabel(a.opensOn)}
                        </span>
                      ) : null}
                    </td>
                    {mutable ? (
                      <td className={`${ctd} ${act} max-md:block max-md:p-0`}>
                        {a.invitedOutside ? (
                          <Button size="sm" variant="quiet" disabled={clear.isLoading} onClick={() => clear.mutate(a)}>
                            Clear
                          </Button>
                        ) : (
                          <span className="inline-flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => setMessage({ kind: 'invitation', audience: a })}>
                              Prepare
                            </Button>
                            <Button size="sm" variant="quiet" onClick={() => setOutside(a)}>
                              Invited elsewhere
                            </Button>
                          </span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {OTHER_MESSAGES.map((m) => (
                <tr key={m.kind} className={stackRow}>
                  <td className={`${ctd} font-semibold max-md:block max-md:p-0`}>{label(m.kind)}</td>
                  <td className={stackCell} data-label="To">
                    {m.to}
                    <span className={cem}>{m.what}</span>
                  </td>
                  <td className={`${stackCell} md:text-right max-md:hidden`} data-label="On the list">
                    <span className="text-ink-3">–</span>
                  </td>
                  <td className={`${stackCell} max-md:hidden`} data-label="Sent">
                    <span className="text-ink-3">–</span>
                  </td>
                  {mutable ? (
                    <td className={`${ctd} ${act} max-md:block max-md:p-0`}>
                      <Button size="sm" onClick={() => setMessage({ kind: m.kind })}>
                        Prepare
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          {clear.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(clear.error, 'Could not save.')}</ErrorNotice>
            </div>
          ) : null}
          <p className="text-small text-ink-3 mt-3 max-w-[72ch]">
            The preview shows who is skipped and why: people already invited, and addresses that asked not to get bulk
            email. Every email sent is kept in the message log.
          </p>
        </PanelBody>
      </Panel>
      {message ? (
        <MessageModal
          event={detail.event}
          kind={message.kind}
          audience={message.audience}
          onClose={() => setMessage(null)}
          onSent={refresh}
        />
      ) : null}
      {outside ? (
        <InvitedOutside
          eventId={detail.event.id}
          audience={outside}
          onClose={() => setOutside(null)}
          onSaved={() => {
            setOutside(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function InvitedOutside({
  eventId,
  audience,
  onClose,
  onSaved,
}: {
  eventId: string;
  audience: Audience;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [on, setOn] = useState(torontoToday());
  const [via, setVia] = useState('');
  const save = useMutation(
    async (value: { on: string; via: string }) =>
      (await api.patch(`/events/${eventId}/audiences/${audience.key}`, { invitedOutside: value })).data,
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title="Invited elsewhere"
      why={`Record that “${audience.name}” was invited outside the platform, so its invitation isn’t prepared here.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" disabled={!via.trim() || save.isLoading} onClick={() => save.mutate({ on, via })}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <div className={pGrid}>
        <PField title="Date">
          <input type="date" className={inputClass} value={on} onChange={(e) => setOn(e.target.value)} />
        </PField>
        <PField title="How" hint={via.trim() ? undefined : 'Needed to save.'}>
          <input
            className={inputClass}
            placeholder="For example, WordPress form, Eventbrite"
            value={via}
            onChange={(e) => setVia(e.target.value)}
          />
        </PField>
      </div>
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- documents

/** "a, b and c" */
const andList = (items: string[]) =>
  items.length < 2 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** A gap as a thing still needed: "a parking text", "key contacts", "Zoom links on its sessions". */
function neededThing(gap: string) {
  const t = gapText(gap, true);
  return /\b\w*[^s\W]s\b/.test(t) ? t : `a ${t}`;
}

/** The Documents tab's opening sentence: where the information packages stand and what they still need. */
function packagesLead(pkgs: { publishedAt: string | null; preparedAt: string | null; gaps: string[] }[]) {
  const n = pkgs.length;
  if (!n) return 'No information packages yet.';
  const pub = pkgs.filter((p) => p.publishedAt).length;
  const prepared = pkgs.filter((p) => p.preparedAt || p.publishedAt).length;
  const first =
    n === 1
      ? pub
        ? 'The information package is published.'
        : prepared
          ? 'The information package is prepared and not published yet.'
          : 'The information package is not prepared yet.'
      : pub === n
        ? `All ${n} information packages are published.`
        : pub
          ? `${pub} of the ${n} information packages are published.`
          : prepared === n
            ? `All ${n} information packages are prepared and none is published.`
            : prepared
              ? `${prepared} of the ${n} information packages are prepared and none is published.`
              : `None of the ${n} information packages is prepared or published yet.`;
  const waiting = pkgs.filter((p) => !p.publishedAt);
  if (!waiting.length) return first;
  const common = waiting[0].gaps.filter((g) => waiting.every((p) => p.gaps.includes(g)));
  if (common.length) {
    const who = n === 1 ? 'It' : waiting.length === n ? 'Each' : waiting.length === 1 ? 'The other' : 'The others';
    const verb = who === 'The others' ? 'need' : 'needs';
    return `${first} ${who} still ${verb} ${andList(common.map(neededThing))}.`;
  }
  const gapped = waiting.filter((p) => p.gaps.length).length;
  return gapped ? `${first} ${gapped} still ${gapped === 1 ? 'needs' : 'need'} something: see Still missing.` : first;
}

function Documents({ detail, mutable }: { detail: EventDetail; mutable: boolean; refresh: () => void }) {
  const eventId = detail.event.id;
  const info = useEventInfo(eventId);
  const [texts, setTexts] = useState(false);
  // The main action answers the opening sentence; PheedLoop's files stay in their own section below.
  const primary =
    mutable && parkingMissingEverywhere(info.data) ? (
      <Button variant="primary" onClick={() => setTexts(true)}>
        Add the parking and arrival texts
      </Button>
    ) : null;
  return (
    <>
      <Lead
        lead={info.data ? packagesLead(info.data.packages) : undefined}
        sub="Everything you print, publish or hand to someone else: attendee packages, lists for the hotel, caterer and the door, agenda documents and the PheedLoop files. Each opens in a new tab to print or save as PDF."
      >
        {primary}
      </Lead>
      {texts ? <SharedTextsDialog eventId={eventId} onClose={() => setTexts(false)} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- check-in links

function CheckinLinks({
  detail,
  mutable,
  refresh,
  day,
}: {
  detail: EventDetail;
  mutable: boolean;
  refresh: () => void;
  day?: string;
}) {
  const [labelText, setLabelText] = useState('');
  const [issued, setIssued] = useState<CheckinLink | null>(null);
  const issue = useMutation(
    async () =>
      (await api.post(`/events/${detail.event.id}/checkin-links`, { label: labelText || null })).data
        .data as CheckinLink,
    {
      onSuccess: (l) => {
        setIssued(l);
        setLabelText('');
        refresh();
      },
    },
  );
  const revoke = useMutation(async (id: string) => api.post(`/events/checkin-links/${id}/revoke`), {
    onSuccess: refresh,
  });
  return (
    <div className={`grid grid-cols-1 gap-5 ${day ? '' : 'lg:grid-cols-2'}`}>
      <Panel>
        <PanelHeader title="Check-in links" />
        <PanelBody>
          <Help>
            A link opens the check-in page on a phone or tablet: search by name or scan the nametag code, add walk-ins.
            It shows names, affiliations and statuses only, works from midnight on the first day to midnight after the
            last, and keeps working offline (taps sync when the connection is back). Issue one per device so you can
            turn one off.
          </Help>
          {mutable ? (
            <div className="flex gap-2 mt-3">
              <input
                className={inputClass}
                placeholder="Label, e.g. Front desk tablet"
                value={labelText}
                onChange={(e) => setLabelText(e.target.value)}
              />
              <Button disabled={issue.isLoading} onClick={() => issue.mutate()}>
                Issue link
              </Button>
            </div>
          ) : (
            <InfoNotice>Admins issue and revoke check-in links.</InfoNotice>
          )}
          {issued?.url ? (
            <div className="mt-3 bg-paper p-3 text-small">
              <div className="font-semibold">Copy this link now; it is not shown again.</div>
              <div className="break-all mt-1 font-mono">{issued.url}</div>
              <Button size="sm" className="mt-2" onClick={() => navigator.clipboard?.writeText(issued.url ?? '')}>
                Copy
              </Button>
            </div>
          ) : null}
          {issue.error || revoke.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(issue.error || revoke.error, 'Could not save.')}</ErrorNotice>
            </div>
          ) : null}
          <ul className="text-table mt-4">
            {detail.links.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 border-b border-line py-2">
                <div>
                  <div>
                    {l.label ?? 'Link'}{' '}
                    <Pill tone={l.state === 'valid' ? 'healthy' : l.state === 'not_yet' ? 'planned' : 'inactive'}>
                      {label(l.state)}
                    </Pill>
                  </div>
                  <div className="text-small text-ink-2">
                    {l.checkins} check-in(s)
                    {l.lastUsedAt
                      ? ` · last used ${new Date(l.lastUsedAt).toLocaleString('en-CA', { timeZone: 'America/Toronto', timeStyle: 'short', dateStyle: 'short' })}`
                      : ''}{' '}
                    · until{' '}
                    {new Date(l.expiresAt).toLocaleString('en-CA', {
                      timeZone: 'America/Toronto',
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </div>
                </div>
                {mutable && !l.revokedAt && l.state !== 'expired' ? (
                  <Button size="sm" variant="destructive" onClick={() => revoke.mutate(l.id)}>
                    Turn off
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </PanelBody>
      </Panel>
      {day ? null : (
        <Panel>
          <PanelHeader title="Live counts" note="refreshes every 20 s" />
          <PanelBody>
            <LiveCounts detail={detail} />
          </PanelBody>
        </Panel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- after the event

function ZoomUpload({ eventId, onClose, onDone }: { eventId: string; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<{ name: string; content: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  type Result = { parsed: number; matched: number; created: number; unmatched: number; warnings: string[] };
  const preview = useMutation(
    async (f: { name: string; content: string }) =>
      (await api.post(`/events/${eventId}/zoom`, { fileName: f.name, content: f.content })).data.data as Result,
  );
  const confirm = useMutation(
    async () =>
      (await api.post(`/events/${eventId}/zoom`, { fileName: file?.name, content: file?.content, confirm: true })).data
        .data as Result,
    { onSuccess: onDone },
  );
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(null);
    setFileError(null);
    preview.reset();
    if (!f) return;
    if (SPREADSHEET.test(f.name)) {
      setFileError(
        'This is an Excel file. Open it in Excel, choose File → Save As → CSV UTF-8 (.csv), then upload the .csv.',
      );
      return;
    }
    const content = await f.text();
    setFile({ name: f.name, content });
    preview.mutate({ name: f.name, content });
  };
  const result = confirm.data ?? preview.data;
  return (
    <Modal
      title="Online attendance (Zoom attendee report)"
      onClose={onClose}
      footer={
        confirm.data ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button disabled={!preview.data || confirm.isLoading} onClick={() => confirm.mutate()}>
              Record attendance
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      <p className="text-small">
        The same importer as the Webinars page: members matched by email, everyone else goes to the unmatched-attendee
        triage.
      </p>
      {!confirm.data ? <input type="file" accept=".csv,text/csv" onChange={pick} className="mt-3 text-small" /> : null}
      {fileError ? (
        <div className="mt-3">
          <ErrorNotice>{fileError}</ErrorNotice>
        </div>
      ) : null}
      {preview.error || confirm.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(preview.error || confirm.error, 'Could not read the report.')}</ErrorNotice>
        </div>
      ) : null}
      {result ? (
        <div className="mt-4 border-t border-line pt-3 text-table">
          <div className="text-small text-ink-2 mb-1">{confirm.data ? 'Recorded' : 'Preview, nothing saved yet'}</div>
          {result.parsed} in the report · {result.matched} members · {result.unmatched} not members
          {confirm.data ? ` · ${result.created} new attendance records` : ''}
          {result.warnings.map((w) => (
            <div key={w} className="text-ink-2 text-small">
              {w}
            </div>
          ))}
        </div>
      ) : null}
    </Modal>
  );
}

function StatsView({ stats }: { stats: EventStats }) {
  const s = stats.stats;
  const e = s.equity;
  return (
    <>
      <div className="flex gap-8 mb-4">
        <div>
          <div className="font-semibold text-[30px]">{fmt(s.attendees)}</div>
          <div className="text-small text-ink-2">in person</div>
        </div>
        <div>
          <div className="font-semibold text-[30px]">{fmt(s.virtual)}</div>
          <div className="text-small text-ink-2">online only</div>
        </div>
        {s.byDay.map((d) => (
          <div key={d.date}>
            <div className="font-semibold text-[30px]">{fmt(d.count)}</div>
            <div className="text-small text-ink-2">{dayLabel(d.date)}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
        <CountTable title="Audience" rows={s.byAudience} />
        <CountTable title="Role" rows={s.byRole} />
        <CountTable title="Region" rows={s.byRegion} />
        <CountTable title="Career stage" rows={s.byCareerStage} />
        <CountTable title="Discipline" rows={s.byDiscipline} />
      </div>
      <div className="mt-5 border-t border-line pt-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="text-small text-ink-2 mb-1">Equity · {e.answeredBy} answered</div>
          <ul className="text-table">
            {e.booleans.map((b) => (
              <li key={b.key} className="flex justify-between border-b border-line py-1">
                <span>{b.label}</span>
                <span>
                  {fmt(b.yes)} <span className="text-ink-2">of {b.answered}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <CountTable title="Gender identity" rows={e.genderIdentity.items} />
          </div>
        </div>
        <div>
          <div className="text-small text-ink-2 mb-1">
            Evaluation · {stats.ratings.responses} response(s) linked to this event
          </div>
          <ul className="text-table">
            {stats.ratings.numeric
              .filter((r) => r.answered > 0)
              .map((r) => (
                <li key={r.key} className="flex justify-between border-b border-line py-1">
                  <span>{r.label}</span>
                  <span>{r.mean ?? '—'}</span>
                </li>
              ))}
          </ul>
          {stats.ratings.responses ? null : (
            <p className="text-small text-ink-2 mt-2">Link the evaluation form on the Overview tab.</p>
          )}
        </div>
      </div>
    </>
  );
}

function After({
  detail,
  mutable,
  refresh,
  part,
}: {
  detail: EventDetail;
  mutable: boolean;
  refresh: () => void;
  part: 'attendance' | 'report';
}) {
  const see = useCanSee();
  const showAll = useContext(ShowAllTabs);
  const content = useEventContent(detail.event.id);
  const ended = new Date(detail.event.endsAt) <= new Date();
  const showPanel = (panel: string) =>
    panelVisible(detail.event.kind, panel, content.data, { showAll, hybrid: detail.event.mode === 'hybrid', ended });
  const Upload = slotFor('attendanceUpload');
  const [zoom, setZoom] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const stats = useQuery<EventStats>(
    ['event-stats', detail.event.id],
    async () => (await api.get(`/events/${detail.event.id}/stats`)).data.data,
  );
  const close = useMutation(
    async (dryRun: boolean) =>
      (await api.post(`/events/${detail.event.id}/close`, { dryRun })).data.data as {
        dryRun: boolean;
        noShows: number;
        reimbursements: number;
        entriesCreated: number;
      },
    {
      onSuccess: (d) => {
        if (!d.dryRun) refresh();
      },
    },
  );
  const generate = async () => {
    setStatsError(null);
    const tab = openBlankTab();
    try {
      const res = await api.post(`/events/${detail.event.id}/stats`);
      showPrintable(tab, res.data.data.document.html);
      refresh();
    } catch (err) {
      tab?.close();
      setStatsError(errorMessage(err, 'Could not generate the stats.'));
    }
  };
  return part === 'attendance' ? (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {showPanel('after.attendance.close') ? (
          <Panel>
            <PanelHeader title="Close the event" note={detail.event.closedAt ? 'closed' : undefined} />
            <PanelBody className="text-small">
              <Help>
                Confirmed invitees never checked in become no-shows; funded attendees who travelled get a reimbursement
                entry owed in the payments ledger (York Concur is where the claim happens; banking details never come to
                the platform). Safe to run again.
              </Help>
              {mutable ? (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" disabled={!ended || close.isLoading} onClick={() => close.mutate(true)}>
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!ended || !close.data?.dryRun || close.isLoading}
                    onClick={() => close.mutate(false)}
                  >
                    Close the event
                  </Button>
                </div>
              ) : null}
              {!ended ? <p className="text-ink-2 mt-2">Available once the last day has ended.</p> : null}
              {close.data ? (
                <p className="mt-3">
                  {close.data.dryRun ? 'Would mark' : 'Marked'} {close.data.noShows} no-show(s) ·{' '}
                  {close.data.reimbursements} reimbursement(s)
                  {close.data.dryRun
                    ? ''
                    : ` · ${close.data.entriesCreated} new ledger entr${close.data.entriesCreated === 1 ? 'y' : 'ies'}`}
                </p>
              ) : null}
              {close.error ? (
                <div className="mt-3">
                  <ErrorNotice>{errorMessage(close.error, 'Could not close the event.')}</ErrorNotice>
                </div>
              ) : null}
              <p className="mt-4 text-ink-2">
                Activity prizes: select the winners on the Invitees tab, then Gift cards. Reimbursements and gift cards
                are followed on the{' '}
                {see('payments') ? (
                  <Link className="underline" to={`/payments?eventId=${detail.event.id}`}>
                    Payments
                  </Link>
                ) : (
                  'Payments'
                )}{' '}
                page.
              </p>
            </PanelBody>
          </Panel>
        ) : null}
        {showPanel('after.attendance.zoom') ? (
          <Panel>
            <PanelHeader title="Online attendance" />
            <PanelBody className="text-small">
              <Help>
                For a hybrid event, upload the Zoom attendee report; members count as online attendees in the stats.
                {detail.event.zoomWebinarId ? ` Linked to Zoom webinar ${detail.event.zoomWebinarId}.` : ''}
              </Help>
              {mutable ? (
                <Button size="sm" className="mt-3" onClick={() => setZoom(true)}>
                  Upload Zoom report
                </Button>
              ) : null}
            </PanelBody>
          </Panel>
        ) : null}
        {Upload ? (
          <div className="lg:col-span-2">
            <Upload detail={detail} mutable={mutable} refresh={refresh} />
          </div>
        ) : null}
      </div>
      {zoom ? (
        <ZoomUpload
          eventId={detail.event.id}
          onClose={() => setZoom(false)}
          onDone={() => {
            refresh();
            stats.refetch();
          }}
        />
      ) : null}
    </>
  ) : (
    <>
      {showPanel('after.report.summary') ? (
        <Panel>
          <PanelHeader
            title="Attendance and evaluation"
            note={
              detail.event.statsGeneratedAt
                ? `generated ${dayLabel(detail.event.statsGeneratedAt.slice(0, 10))}`
                : undefined
            }
          >
            {mutable ? (
              <Button size="sm" onClick={generate}>
                Printable summary
              </Button>
            ) : null}
          </PanelHeader>
          <PanelBody>
            {statsError ? <ErrorNotice>{statsError}</ErrorNotice> : null}
            {stats.isLoading ? (
              <SkeletonRows rows={4} />
            ) : stats.error ? (
              <ErrorNotice>Could not load the stats.</ErrorNotice>
            ) : stats.data ? (
              <StatsView stats={stats.data} />
            ) : null}
          </PanelBody>
        </Panel>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- pages

/** The Timeline's opening sentence: steps done, past their date, and due in the next two weeks. */
function timelineLead(c: ReturnType<typeof timelineCounts>) {
  if (!c.total) return undefined;
  const done = `${fmt(c.done)} of the ${fmt(c.total)} planning steps ${c.done === 1 ? 'is' : 'are'} done${
    c.skipped ? ` and ${fmt(c.skipped)} skipped` : ''
  }.`;
  const late = !c.late
    ? 'None is past its due date'
    : `${fmt(c.late)} ${c.late === 1 ? 'is past its' : 'are past their'} due date`;
  const soon = !c.soon ? 'none is due' : `${fmt(c.soon)} ${c.soon === 1 ? 'is' : 'are'} due`;
  return `${done} ${late}, and ${soon} in the next two weeks.`;
}

function Timeline({ detail, mutable, refresh }: PanelProps) {
  const list = useChecklist('event', detail.event.id);
  return (
    <>
      <Lead
        lead={list.data ? timelineLead(timelineCounts(list.data)) : undefined}
        sub="The in-person event checklist. Due dates count from the first day; after-event steps count from the last. Every step belongs to the Program Manager unless another owner is shown."
      />
      <Checklist
        subjectType="event"
        subjectId={detail.event.id}
        mutable={mutable}
        onChange={refresh}
        layout="grouped"
      />
    </>
  );
}

function EventSetup(props: PanelProps) {
  return <Overview {...props} />;
}

registerPanel('before', 'people', Invitees);
registerPanel('before', 'emails', Invitations);
registerPanel('before', 'timeline', Timeline);
registerPanel('before', 'documents', Documents);
registerPanel('after', 'attendance', (p: PanelProps) => <After {...p} part="attendance" />);
registerPanel('after', 'report', (p: PanelProps) => <After {...p} part="report" />);
registerSlot('overviewSetup', EventSetup);
registerSlot('checkinLinks', CheckinLinks);

function EventPage({ eventId }: { eventId: string }) {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [nextYear, setNextYear] = useState(false);
  const navigate = useNavigate();
  const detail = useQuery<EventDetail>(
    ['event', eventId],
    async () => (await api.get(`/events/${eventId}`)).data.data,
    { refetchInterval: 20000 },
  );
  const refresh = () => {
    queryClient.invalidateQueries('event');
    queryClient.invalidateQueries('events');
    queryClient.invalidateQueries('event-stats');
    queryClient.invalidateQueries('event-dashboard');
    queryClient.invalidateQueries('event-content');
    queryClient.invalidateQueries('event-followups');
    queryClient.invalidateQueries('checklists');
  };
  if (detail.isLoading) return <SkeletonRows rows={8} />;
  if (detail.error || !detail.data)
    return <ErrorNotice>{errorMessage(detail.error, 'Could not load the event.')}</ErrorNotice>;
  const e = detail.data.event;
  const editor = mutable;
  const writable = editor && !e.locked;
  if (e.kind === 'outreach') return <OutreachPage detail={detail.data} mutable={writable} refresh={refresh} />;
  return (
    <>
      {(() => {
        const banner = e.locked ? (
          <LockBanner
            eventId={e.id}
            lockedFrom={e.lockedFrom}
            isSuperAdmin={user?.role === 'super_admin'}
            refresh={refresh}
          />
        ) : null;
        const aside = editor ? (
          <div className="flex flex-wrap gap-2">
            {e.kind !== 'pi_meeting' && new Date(e.endsAt) < new Date() ? (
              <Button onClick={() => setNextYear(true)}>Start next year&rsquo;s event from this one</Button>
            ) : null}
            {writable ? <Button onClick={() => setEdit(true)}>Edit event</Button> : null}
          </div>
        ) : undefined;
        return e.kind === 'pi_meeting' ? (
          <>
            {banner ? <div className="mb-4">{banner}</div> : null}
            <PiMeetingPage detail={detail.data} mutable={writable} aside={aside} />
          </>
        ) : (
          <EventShell detail={detail.data} mutable={writable} refresh={refresh} aside={aside} banner={banner} />
        );
      })()}
      {nextYear ? (
        <StartNextYear
          event={e}
          onClose={() => setNextYear(false)}
          onCreated={(id) => {
            setNextYear(false);
            navigate(`/events/${id}`);
          }}
        />
      ) : null}
      {edit ? (
        <EventForm
          event={e}
          onClose={() => setEdit(false)}
          onSaved={() => {
            setEdit(false);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function EventList() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const navigate = useNavigate();
  const [create, setCreate] = useState(false);
  const [outreach, setOutreach] = useState(false);
  const list = useQuery<EventListItem[]>('events', async () => (await api.get('/events')).data.data);
  return (
    <>
      <PageHeader
        title="Events"
        lead={list.data?.length ? listLead(list.data) : undefined}
        sub="The national meeting, regional meetings, PI meetings and outreach talks, newest first. Trends across years are at the bottom."
        aside={
          <>
            <a
              href="/guides/events-quick-start.html"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center min-h-[44px] px-1 font-semibold text-[15px] leading-5 text-accent underline underline-offset-[3px] hover:text-ink"
            >
              Quick-start guide
            </a>
            {mutable ? <Button onClick={() => setOutreach(true)}>Log an outreach talk</Button> : null}
            {mutable ? (
              <Button variant="primary" className="max-md:w-full" onClick={() => setCreate(true)}>
                New event
              </Button>
            ) : null}
          </>
        }
      />
      {list.isLoading ? (
        <SkeletonRows rows={4} />
      ) : list.error ? (
        <ErrorNotice>Could not load the events. Reload the page to try again.</ErrorNotice>
      ) : !list.data?.length ? (
        <EmptyState title="No in-person events yet">
          Create the next national event to start its checklist and invitation list.
        </EmptyState>
      ) : (
        <>
          <EventListSections events={list.data} onLogOutreach={mutable ? () => setOutreach(true) : undefined} />
          <EventInsights />
        </>
      )}
      {create ? (
        <EventForm
          event={null}
          onClose={() => setCreate(false)}
          onOutreach={() => {
            setCreate(false);
            setOutreach(true);
          }}
          onSaved={(id) => {
            setCreate(false);
            navigate(`/events/${id}`);
          }}
        />
      ) : null}
      {outreach ? (
        <OutreachForm
          event={null}
          onClose={() => setOutreach(false)}
          onSaved={(id) => {
            setOutreach(false);
            navigate(`/events/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

export default function Events() {
  const { eventId } = useParams<{ eventId?: string }>();
  return eventId ? <EventPage eventId={eventId} /> : <EventList />;
}
